import Vue from 'vue'
import App from './App.vue'
import Vuex from 'vuex'
import router, { SCHEDULE_ONLY_ALLOWED_ROUTES } from './router'
import { resolveScheduleEnabled } from './components/schedule/scheduleFeatureFlags'
import './plugins/base'
import './plugins/vee-validate'
import vuetify from './plugins/vuetify'
import VueMeta from 'vue-meta'
import i18n from './i18n'
import helpers from '@/mixins/helpers'
import { MODEL_EXTENSIONS, modelColorName, modelThumbnailDetails } from '@/utils/modelThumbnail'
import { projectTax } from '@/utils/stripeTaxCalculation'
import { isUsableCartProject } from '@/utils/placedOrder'
import { NEW_APP_ONLY_ROUTES, latchNewAppForStaff, shouldAutoOptIn } from '@/utils/vue3DefaultOn'
import { switchToNewApp } from '@/utils/switchToNewApp'
import { hasVue3Cookie, hasLegacyCookie, clearVue3Cookie } from '@/utils/uiVersionCookie'
import upload from './store/modules/upload'
import search from './store/modules/search'
import QRious from 'qrious'
import Axios from 'axios'
import _ from 'lodash';

if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_MODE !== 'preview') {
  require('./registerServiceWorker')
}
import { PushNotifications } from '@capacitor/push-notifications'
import { Badge } from '@capawesome/capacitor-badge'
import { onAuthStateChanged, signOut, deleteUser } from 'firebase/auth'
import { db, databaseProduction, databaseDevelopment, auth, storage } from './firebaseConfig'
import { doc, updateDoc, onSnapshot, setDoc, query, collection, orderBy, where, or, and, limit, getDoc, getDocs, arrayUnion, arrayRemove, connectFirestoreEmulator } from 'firebase/firestore'
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'
import { ref, getDownloadURL, uploadBytesResumable } from 'firebase/storage'
import { Capacitor } from '@capacitor/core'
import VueObserveVisibility from 'vue-observe-visibility'
import { inject } from '@vercel/analytics'
import { DeviceUUID } from 'device-uuid'
import { StatusBar, Style } from '@capacitor/status-bar'
import VueGtag from 'vue-gtag'
import { USE_DEV_API, USE_DEV_FIREBASE, FORCE_ENTERPRISE, FORCE_ENTERPRISE_ID, STAFF_IDLE_TIMEOUT_IMMUNE_USER_IDS, DEVELOPER_USER_IDS } from '../config.js'
import { downloadBlob } from './composables/exportDataHelpers';
import notificationSound from '@/services/notificationSound'

Vue.use(Vuex)

const store = new Vuex.Store({
  modules: {
    upload,
    search,
  }
})

const APP_VERSION = '2.5.3'
const DIST_VERSION = '1'

Vue.use(VueMeta)
Vue.use(VueObserveVisibility)

if(process.env.VUE_APP_MODE === 'preview'){console.log('process.env.VUE_APP_MODE:', process.env.VUE_APP_MODE)}

Vue.config.performance = process.env.NODE_ENV !== 'production'
Vue.config.productionTip = false

// Initialize Vercel analytics
inject()

function setTheme (theme) {
  if (theme) {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('prefs_brand', theme)
  } else {
    document.documentElement.removeAttribute('data-theme')
    localStorage.removeItem('prefs_brand')
  }
}

// Get default enterprise for domain
function getDomainEnterprise () {
  return httpsCallable(functions, 'resolveEnterprise')({
    domain: window.location.origin,
    // resolveEnterprise validates this as an optional boolean, and the callable
    // client encodes undefined as null — so send the guaranteed-boolean accessor
    // rather than the deprecated Capacitor.isNative property.
    mobile: Capacitor.isNativePlatform(),
    pathname: window.location.pathname,
  }).then(res => res.data)
}

// Determine which enterprise to use
function getEnterprise () {
  const override = window.localStorage.getItem('enterpriseOverride')
  if (FORCE_ENTERPRISE) {
    if(process.env.VUE_APP_MODE === 'preview') {
      console.log('FORCE_ENTERPRISE:', FORCE_ENTERPRISE, 'FORCE_ENTERPRISE_ID:', FORCE_ENTERPRISE_ID)
    }
    return getDoc(doc(db, 'enterprises', FORCE_ENTERPRISE_ID)).then(snap => {
      if (snap.exists()) return { id: snap.id, enterprise: snap.data(), preloadedTasks: [], }
      else return getDomainEnterprise()
    })
  } else if (override) {
    return getDoc(doc(db, 'enterprises', override)).then(snap => {
      if (snap.exists()) return { id: snap.id, enterprise: snap.data(), preloadedTasks: [], }
      else {
        window.localStorage.removeItem('enterpriseOverride')
        return getEnterprise()
      }
    })
  } else return getDomainEnterprise()

}

// Setup firebase functions, and
// get enterprise for this session
const timeStart = Date.now()
const functions = getFunctions()
if (process.env.NODE_ENV !== 'production') {
  // connectFirestoreEmulator(db, 'localhost', 8888)
  // connectFunctionsEmulator(functions, 'localhost', 5001)
}
getEnterprise()
  .then(enterprise => {
    window.enterpriseID = enterprise.id
    window.enterprise = enterprise
    window.preloadedTasks = enterprise.preloadedTasks

    // Load google tag manager
    if (enterprise.enterprise.googleAnalyticsMeasurementID) {
      Vue.use(VueGtag, {
        config: { id: enterprise.enterprise.googleAnalyticsMeasurementID }
      })
      // Use this.$gtag.event('login', { method: 'Google' }) for recording events
    }

    // Handle new auth state change
    onAuthStateChanged(auth, async function (user) {
      const previousAuth = app ? app.currentUser : null
      const userToken = user ? await auth.currentUser.getIdTokenResult() : null

      // Send user info to Vue app scope
      if (app) app.userToken = userToken
      if (app) app.currentUser = user

      // Send user info to window scope
      window.userToken = userToken
      window.currentUser = user

      // A user coming through Login is an intentional fresh session, not a stale restored browser session.
      if (app && user && app.$route.name === 'Login') app.staffIdleTimeoutFreshLogin = true

      // Route user away from login if needed
      if (app && user && app.$route.name === 'Login' && !app.showProjectPreview) {
        app.$router.push(app.$route.query.redirect || (app.isStaff ? '/' : '/projects'))
      }

      // Launch app if not yet started
      if (!app) {
        const tempTheme = localStorage.getItem('prefs_brand')
        if (tempTheme) setTheme(tempTheme)
        document.getElementById('intital-loading-icon').style.opacity = 0
        initializeVueApp(enterprise, user, userToken, tempTheme)
      }

      // Reset user profile listener
      if (app.subscriptions.profile) app.subscriptions.profile()
      app.subscriptions.profile = null

      // Get user-relevant data
      if (user) {
        // Ask for push notification permission
        if (Capacitor.isNativePlatform()) {
          // Request permission to use push notifications
          // iOS will prompt user and return if they granted permission or not
          // Android will just grant without prompting
          PushNotifications.requestPermissions().then(result => {
            if (result.receive === 'granted') {
              // Register with Apple / Google to receive push via APNS/FCM
              PushNotifications.register()
            } else {
              // Show some error
              console.log('Error requesting notification permissions', result)
            }
          })
        }

        // Record device/version info
        try {
          const deviceID = new DeviceUUID().get()
          const deviceInfo = new DeviceUUID().parse()
          setDoc(
            doc(db, 'enterprises', app.enterpriseID, 'users', user.uid, 'deviceStatistics', deviceID),
            Object.assign(JSON.parse(JSON.stringify(deviceInfo)), { loaded: Date.now(), version: app.version }),
            { merge: true },
          ).catch(err => console.log('Error recording device info:', err))
        } catch (err) {
          console.log('Error verifying version info:', err)
        }

        // Update push notification registration tokens to account
        if (app.notificationToken) {
          setDoc(
            doc(db, 'users', app.currentUser.uid, 'devices', app.notificationToken),
            { active: true, dateModified: Date.now() },
            { merge: true },
          ).catch(err => console.log('Error updating notification token:', err))
        }

        // Reset user profile listener
        app.subscriptions.profile = onSnapshot(
          doc(db, 'enterprises', app.enterpriseID, 'users', user.uid),
          docSnap => {
            if (docSnap.data()) {
              console.log('[auth profile snapshot]', {
                enterpriseID: app.enterpriseID,
                userID: user.uid,
                tokenExpired: !!docSnap.data().tokenExpired,
                email: docSnap.data().email || null,
              })
              // Import user data if up-to-date
              if (!app.userProfile || app.userProfile.dateModified < (docSnap.data().dateModified ? docSnap.data().dateModified : 1)) {
                app.state.skipChange.userProfile = true
                app.userProfile = docSnap.data()
              }

              // Refresh token if requested
              if (docSnap.data().tokenExpired) app.resetToken(user)

              // Fill in missing preferences
              if (!app.userProfile.email) Vue.set(app.userProfile, 'email', app.currentUser.email)
              if (!app.userProfile.prefs_dashboard) Vue.set(app.userProfile, 'prefs_dashboard', 'production')
              if (app.userProfile.prefs_brand && !app.enterprise.brands.find(b => b.id === app.userProfile.prefs_brand && b.public)) Vue.set(app.userProfile, 'prefs_brand', '')
              if (app.userProfile.prefs_taskView === false) Vue.set(app.userProfile, 'prefs_inlineTasks', true)
              if (typeof app.userProfile.prefs_useDarkMode !== 'undefined') {
                app.darkModePreference = app.userProfile.prefs_useDarkMode
              } else {
                Vue.set(app.userProfile, 'prefs_useDarkMode', app.darkModePreference)
              }
              if (!app.userProfile.prefs_messageTypes) {
                app.userProfile.prefs_messageTypes = {
                  'message': { text: 'Messages', value: 'message', enabled: true },
                  'event': { text: 'Events', value: 'event', enabled: true  },
                  'email': { text: 'Emails', value: 'email', enabled: true  },
                  'machineEvent': { text: 'Machine Events', value: 'machineEvent', enabled: true },
                }
              }
              if (app.isDev) {
                app.superUserEnabled = !!app.userProfile.prefs_superUser
              }
              if (!app.subscriptions.currentEnterpriseStaffAccess) app.listenToCurrentEnterpriseStaffAccess(user.uid)
            } else {
              setDoc(doc(db, 'enterprises', app.enterpriseID, 'users', user.uid), { email: user.email }, { merge: true }).catch(err => console.log('Error creating user profile:', err))
            }
          },
          err => console.log('Error in user profile snapshot listening:', err),
        )

        app.listenToNotifications()
        if (!app.subscriptions.currentEnterpriseStaffAccess) app.listenToCurrentEnterpriseStaffAccess(user.uid)
        if (!app.subscriptions.currentEnterpriseScheduleMember) app.listenToCurrentEnterpriseScheduleMember(user.uid)

        // Track per-user "last read" timestamps for support/ticket chats (unread badges).
        if (!app.subscriptions.supportReads) {
          app.subscriptions.supportReads = onSnapshot(
            doc(db, 'supportReads', user.uid),
            snap => { app.supportReads = snap.data() || {} },
            err => console.log('Error listening to supportReads', err),
          )
        }

        if (app.isStaff) app.startStaffSession()
      } else if (previousAuth) {
        // Route signed-out users back to the public/customer side
        if (!['Submit Files', 'Login', 'ProjectPage', null].includes(app.$route.name) && !app.showProjectPreview) {
          app.$router.replace('/').catch(() => {})
        }
        app.stopAllUserSubscriptions()
        app.currentUser = null
        window.currentUser = null
        app.userToken = null
        window.userToken = null
        app.userProfile = null
        app.currentEnterpriseStaffAccessLoaded = true
        app.currentEnterpriseStaffAccess = 0
        app.currentEnterpriseScheduleMemberLoaded = true
        app.currentEnterpriseScheduleMember = null
        app.staffIdleTimeoutEvaluatedStoredSession = false
        app.staffIdleTimeoutLogoutInProgress = false
        app.staffIdleTimeoutFreshLogin = false
        app.loggingOut = false
        app.management = null
        app.allProjects = null
        app.showProjectPreview = false
        app.state.settingsOpen = false
      }
    })
  })

// Function to initialize main Vue app
let app
function initializeVueApp (startingEnterprise, user, userToken, tempTheme) {
  app = new Vue({
    router,
    vuetify,
    i18n,
    store,
    mixins: [helpers],
    metaInfo () {
      let name = this.brandName || 'formfactories'
      if (this.userNotifications.filter(n => !n.read).length) name += ` ('${this.userNotifications.filter(n => !n.read).length})`

      return {
        title: name,
        titleTemplate: `%s | ${name}`,
        meta: [
          { name: 'application-name', content: name },
          { name: 'apple-mobile-web-app-title', content: name },
          { name: 'msapplication-TileColor', content: this.theme.color },
          { name: 'msapplication-TileImage', content: this.favicon ? this.shrunkImage(this.favicon, 150, 150, 'png') : require('@/assets/favicons/favicon_150.png') },
          { name: 'msapplication-config', content: '@/assets/favicons/incept3d/browserconfig.xml' },
          { name: 'theme-color', content: this.theme.headerColor },
        ],
        link: [
          { rel: 'shortcut icon', sizes: '180x180', href: this.favicon ? this.shrunkImage(this.favicon, 180, 180, 'png') : require('@/assets/favicons/favicon_180.png') },
          { rel: 'apple-touch-icon', sizes: '180x180', href: this.favicon ? this.shrunkImage(this.favicon, 180, 180, 'png') : require('@/assets/favicons/favicon_180.png') },
          { rel: 'icon', type: 'image/png', sizes: '240x240', href: this.favicon ? this.shrunkImage(this.favicon, 240, 240, 'png') : require('@/assets/favicons/favicon_240.png') },
          { rel: 'icon', type: 'image/png', sizes: '192x192', href: this.favicon ? this.shrunkImage(this.favicon, 192, 192, 'png') : require('@/assets/favicons/favicon_192.png') },
          { rel: 'icon', type: 'image/png', sizes: '180x180', href: this.favicon ? this.shrunkImage(this.favicon, 180, 180, 'png') : require('@/assets/favicons/favicon_180.png') },
          { rel: 'icon', type: 'image/png', sizes: '144x144', href: this.favicon ? this.shrunkImage(this.favicon, 144, 144, 'png') : require('@/assets/favicons/favicon_144.png') },
          { rel: 'icon', type: 'image/png', sizes: '128x128', href: this.favicon ? this.shrunkImage(this.favicon, 128, 128, 'png') : require('@/assets/favicons/favicon_128.png') },
          { rel: 'icon', type: 'image/png', sizes: '96x96', href: this.favicon ? this.shrunkImage(this.favicon, 96, 96, 'png') : require('@/assets/favicons/favicon_96.png') },
          { rel: 'icon', type: 'image/png', sizes: '48x48', href: this.favicon ? this.shrunkImage(this.favicon, 48, 48, 'png') : require('@/assets/favicons/favicon_48.png') },
          { rel: 'icon', type: 'image/png', sizes: '32x32', href: this.favicon ? this.shrunkImage(this.favicon, 32, 32, 'png') : require('@/assets/favicons/favicon_32.png') },
          { rel: 'icon', type: 'image/png', sizes: '16x16', href: this.favicon ? this.shrunkImage(this.favicon, 16, 16, 'png') : require('@/assets/favicons/favicon_16.png') },
          // { rel: 'mask-icon', href: this.enterprise.safariPinnedTab || require('@/assets/favicons/incept3d/safari-pinned-tab.svg'), color: this.theme.color },
        ],
      }
    },
    data: function () {
      return {
        version: APP_VERSION,
        bugReportingChat: 'tVmBdZqiDmaCGyIeML4t',
        developerList: [], // dev/superuser docs from the top-level `developers` collection
        developerListLoaded: false, // true once the `developers` snapshot has first resolved
        superUserEnabled: false,
        supportReads: {}, // { [roomKey]: lastReadTimestamp } for support/ticket unread badges
        // Set when a tech-support notification is clicked; the /dev or /support page that
        // mounts reads it to open that exact ticket, then clears it. { enterpriseID, ticketID }
        pendingSupportTicket: null,
        apiPath: USE_DEV_API ? 'https://api.lanalanalana.com' : 'https://api.formfactories.com',
        productionMode: process.env.NODE_ENV === 'production',
        sitePath: document.location.origin,
        db: db,
        auth: auth,
        storage: storage,
        functions: functions,
        publicPath: process.env.BASE_URL,
        showStaffSideBar: false,
        enterpriseRaw: startingEnterprise.enterprise,
        enterpriseID: startingEnterprise.id,
        allTiers: [],
        worker: null,
        memoryHistory: [],
        memorySamples: 600,
        memoryInterval: null,
        workerTasks: {},
        primaryProjectStatus: null,
        initialChat: null,
        droppedFiles: null,
        navigation: null,
        openDesignCollectionChat: null,
        activeUploads: 0,
        uploadManagerTemporarilyHidden: false,
        showProjectPreview: false,
        projectPreviewNumItems: 0,
        production: {},
        currentUser: user,
        userToken: userToken,
        userProfile: null,
        cartOpen: false,
        userNotifications: [],
        starredUserNotifications: [],
        unreadUserNotifications: [],
        userTaskGroups: [],
        userReads: {},
        staffInviteID: null,
        acceptingStaffInvite: false,
        anonymousUserName: '',
        anonymousUserEmail: '',
        quantityPreviews: [1, 5, 10, 50, 100],
        // printerSnapshotBrowser removed: its dialog (Navigation.vue) had no
        // reachable opener once the Camera Feed buttons were commented out, and
        // it rendered snapshots from the retired gateway system.
        printerSnapshotFullscreen: false,
        printerSnapshotFullscreenPrinter: null,
        printerSnapshotFullscreenFamily: null,
        printerSnapshotPrinterTab: 1,
        publishedTasks: startingEnterprise.preloadedTasks,
        taxRates: [],
        machinePanelID: null,
        machinePanelOpen: false,
        machinePanelDefaultTab: 0,
        machinePanelInitialTimeRange: null,
        pageScroll: 0,
        publicConfiguration: {},
        configPublic: {},
        // Flips true on the first config/public snapshot (or its error), so gates
        // that read a Beta setting can tell "off" from "not loaded yet".
        configPublicLoaded: false,
        // enableNewAppForStaff from the FIRST config/public snapshot, null until it lands.
        // Latched, not live: see latchNewAppForStaff.
        newAppForStaffAtLoad: null,
        messageToScrollTo: null,
        messageToScrollToChat: null,
        errors: [],
        visibleTaskListener: null,
        viewableTasks: [],
        simulateTeams: false,
        simulatedTeams: ['public'],
        spaceDown: false,
        spaceHoldDeadline: null,
        messageDrafts: {},
        queuedFailureEvent: null,
        taskElementTypes: [
          {
            name: 'Text',
            type: 'text',
            icon: 'fas fa-align-left',
            active: true,
            simpleMode: true,
            advanced: false,
          },
          {
            name: 'Header',
            type: 'header',
            icon: 'fas fa-heading',
            active: false,
            simpleMode: false,
            advanced: false,
          },
          {
            name: 'Subtitle',
            type: 'subtitle',
            icon: 'mdi-format-header-2',
            active: false,
            simpleMode: false,
            advanced: false,
          },
          {
            name: 'Big Header',
            type: 'h1',
            icon: 'mdi-format-header-1',
            active: true,
            simpleMode: true,
            advanced: false,
          },
          {
            name: 'Medium Header',
            type: 'h2',
            icon: 'mdi-format-header-2',
            active: true,
            simpleMode: false,
            advanced: false,
          },
          {
            name: 'Small Header',
            type: 'h3',
            icon: 'mdi-format-header-3',
            active: true,
            simpleMode: false,
            advanced: false,
          },
          {
            name: 'Todo',
            type: 'todo',
            icon: 'fas fa-check-circle',
            active: true,
            simpleMode: true,
            advanced: false,
          },
          {
            name: 'Time Tracking',
            type: 'timeTracking',
            icon: 'mdi-timer-outline',
            active: true,
            simpleMode: false,
            advanced: true,
          },
          {
            name: 'Bullet',
            type: 'bullet',
            icon: 'mdi-circle',
            active: true,
            simpleMode: true,
            advanced: true,
          },
          {
            name: 'Counter',
            type: 'counter',
            icon: 'fas fa-calculator',
            active: true,
            simpleMode: true,
            advanced: true,
          },
          {
            name: 'Colors',
            type: 'colors',
            icon: 'fas fa-tint',
            active: false,
            simpleMode: false,
            advanced: true,
          },
          {
            name: 'Contents',
            type: 'contents',
            icon: 'fas fa-bookmark',
            active: true,
            simpleMode: false,
            advanced: true,
          },
          {
            name: 'Reference',
            type: 'reference',
            icon: 'fas fa-asterisk',
            active: true,
            simpleMode: false,
            advanced: true,
          },
          {
            name: 'Group',
            type: 'group',
            icon: 'fas fa-object-group',
            active: true,
            simpleMode: false,
            advanced: false,
          },
          {
            name: 'Component',
            type: 'component',
            icon: 'fas fa-cog',
            active: true,
            simpleMode: false,
            advanced: true,
          },
        ],
        salesOptions: null,
        machinesState: {},
        sellingPoints: {},
        management: null,
        workflowsConfig: null,
        allMachineInstances: [],
        currentEnterpriseStaffAccess: null,
        currentEnterpriseStaffAccessLoaded: false,
        /**
         * The signed-in user's own `scheduleEmployees/{uid}` row, or null.
         *
         * This is the ONLY runtime signal for the schedule-only role: no custom
         * claim is minted for it anywhere (the sole claim writer, staffUpdateGen2,
         * fires on `staff/{uid}` writes that this role by design never gets), so
         * the identity has to be read straight off the document.
         *
         * The read is lossy in one way callers must respect: rules allow it only
         * while the row says `schedulerOnly == true && active != false`, so a
         * non-member and a REVOKED member both surface as a permission denial and
         * are indistinguishable here. Both correctly resolve to "not schedule-only".
         */
        currentEnterpriseScheduleMember: null,
        currentEnterpriseScheduleMemberLoaded: false,
        loggingOut: false,
        showAdvanced: true,
        showNotifications: false,
        showTasks: false,
        notificationAmount: 6,
        notificationPage: 2,
        currentTime: Date.now(),
        currentTimeMinutes: Date.now(),
        currentTimeInterval: null,
        currentTimeMinutesInterval: null,
        unreads: {},
        active: {},
        tempTheme: tempTheme,
        hideHeaders: false,
        notificationToken: null,
        organizationPreviewOpen: false,
        organizationPreviewID: null,
        userPreviewOpen: false,
        userPreviewEmail: null,
        projectPreviewOpen: false,
        projectPreviewID: null,
        jobPreviewOpen: false,
        jobPreviewID: null,
        jobPreviewListener: null,
        jobPreviewData: null,
        showAutoEmailConfirmation: false,
        autoEmailConfirmation: null,
        autoEmailConfirmationQueue: [],
        autoEmailConfirmationSending: false,
        autoEmailConfirmationDownloading: false,
        emailEditorShow: false,
        emailEditorProject: null,
        emailEditorTemplate: null,
        staffEnterprisesLoaded: false,
        staffEnterpriseListeners: {},
        staffEnterpriseNotificationListeners: {},
        staffEnterpriseNames: {},
        staffEnterpriseURLs: {},
        staffEnterpriseUnreadNotificationCount: {},
        // Active enterprises, populated only for developers (see startDevTicketFeed). Lets the
        // Notification Center scope a dev to any enterprise's tickets on demand.
        allEnterprises: [], // [{ id, name, url }]
        staffIdleTimeoutListenerActive: false,
        staffIdleTimeoutTimer: null,
        staffIdleTimeoutCountdownTimer: null,
        staffIdleTimeoutWarningOpen: false,
        staffIdleTimeoutWarningSecondsLeft: 60,
        staffIdleTimeoutLogoutInProgress: false,
        staffIdleTimeoutLastActivityWrite: 0,
        staffIdleTimeoutActivityHandler: null,
        staffIdleTimeoutVisibilityHandler: null,
        staffIdleTimeoutEvaluatedStoredSession: false,
        staffIdleTimeoutFreshLogin: false,
        adjustingCustomPricing: false,
        emailAttachmentTags: [
          { text: 'Quote', value: 'quote', condition: project => this.enableProjectPricing },
          { text: 'Packing Slip', value: 'packingSlip', condition: project => true },
          { text: 'Invoice', value: 'invoice', condition: project => this.enableProjectPricing && project.checkout },
          { text: 'Purchase Order', value: 'purchaseOrder', condition: project => project.checkout && project.checkout.files && project.checkout.files.length },
        ],
        emailReplacements: {
          index: {
            description: 'Raw Project Index, e.g. 2123',
            replacer: project => project.index,
          },
          'quote-number': {
            description: 'Client-Formatted Project Number, e.g. LNA-42123',
            replacer: project => `LNA-${parseInt(project.index) + 42000}`,
          },
          quantities: {
            description: 'Project Description, e.g. 2 models, 8 units, White PLA',
            replacer: project => this.projectDescription(project),
          },
          'total-price': {
            description: 'Subtotal of order (with $ prepended)',
            replacer: project => '$' + this.totals(project).subtotal,
          },
          'delivery-info': {
            description: 'If pickup, says "and is ready for pick-up at our address:", otherwise "and is shipping to:"',
            replacer: project => project.checkout ? (project.pickup ? 'and is ready for pick-up at our address:' : 'and is shipping to:') : '',
          },
          'delivery-address': {
            description: 'Lists the delivery address of the customer (or your business address if pickup)',
            replacer: project => project.checkout ? (project.pickup ? this.$root.enterprise.address.street1 + (this.$root.enterprise.address.street2 ? '<br>' + this.$root.enterprise.address.street2 : '') + '<br>' + this.$root.enterprise.address.city + ', ' + this.$root.enterprise.address.state + ' ' + this.$root.enterprise.address.zip
            : project.checkout.shipping.name + '<br>' +
              project.checkout.shipping.address1 +
              (project.checkout.shipping.address2 ? '<br>' + project.checkout.shipping.address2 + '<br>' : '<br>') +
              project.checkout.shipping.city + ', ' +
              project.checkout.shipping.state + ' ' +
              project.checkout.shipping.zip) : '',
          },
          'due-date': {
            description: 'Due date of project',
            replacer: project => this.formatDueDate(project.dueDate)
          },
          'project-url': {
            description: 'The URL to this project',
            replacer: (project, uuid) => document.location.origin + '/projects/' + uuid,
          },
          'project-link': {
            description: 'A link to the project formatted as the project index',
            replacer: (project, uuid) => `<a href="${document.location.origin}/projects/${uuid}">LNA-${parseInt(project.index) + 42000}</a>`,
          },
          'project-email': {
            description: 'The project-level customer email',
            replacer: project => [project.clientEmail].concat(project.extraEmails || []).join(', ') || 'Not provided',
          },
          'project-company': {
            description: 'The project-level customer company',
            replacer: project => project.clientCompany || 'Not provided',
          },
          'project-name': {
            description: 'The project-level customer name',
            replacer: (project, uuid, account) => {
              let name = ''
              if (account && account.staffProfile && account.staffProfile.fullName) name = account.staffProfile.fullName
              else if (project.clientName) name = project.clientName
              else if (account && account.userProfile && account.userProfile.fullName) name = account.userProfile.fullName
              return name
            },
          },
          'project-first-name': {
            description: 'The first word of the project-level customer name',
            replacer: (project, uuid, account) => {
              let name = ''
              if (account && account.staffProfile && account.staffProfile.fullName) name = account.staffProfile.fullName
              else if (project.clientName) name = project.clientName
              else if (account && account.userProfile && account.userProfile.fullName) name = account.userProfile.fullName
              return name.trim().split(' ')[0]
            },
          },
          'project-phone': {
            description: 'The project-level customer phone',
            replacer: project => project.clientPhone || 'Not provided',
          },
          'project-leadtime': {
            description: 'The project lead time',
            replacer: project => project.leadDays || '',
          },
          'project-debug': {
            description: 'Debug data for the nerds',
            replacer: project => JSON.stringify(project),
          },
          'company-email': {
            description: 'Your company support email',
            replacer: () => this.enterprise.email || 'support@formfactories.com',
          },
          'currentuser-fullname': {
            description: 'Full name of the person sending the email',
            replacer: () => this.userProfile.fullName || '',
          },
          'currentuser-firstname': {
            description: 'First name of the person sending the email',
            replacer: () => (this.userProfile.fullName || '').split(' ')[0],
          },
          'currentuser-phone': {
            description: 'Phone of the person sending the email',
            replacer: () => this.userProfile.phone,
          },
          'currentuser-email': {
            description: 'Email of the person sending the email',
            replacer: () => this.currentUser.email,
          },
          'salesperson1-fullname': {
            description: 'Full name of the first salesperson assigned to the project',
            replacer: project => {
              if (project.roles && project.roles.sales && project.roles.sales[0]) {
                this.loadUserInfo(project.roles.sales[0])
                return this.lookup.users[project.roles.sales[0]].fullName
              } else return null
            },
          },
          'salesperson1-firstname': {
            description: 'First name of the first salesperson assigned to the project',
            replacer: project => {
              if (project.roles && project.roles.sales && project.roles.sales[0]) {
                this.loadUserInfo(project.roles.sales[0])
                return (this.lookup.users[project.roles.sales[0]].fullName || '').split(' ')[0]
              } else return null
            },
          },
          'salesperson1-phone': {
            description: 'Phone of the first salesperson assigned to the project',
            replacer: project => {
              if (project.roles && project.roles.sales && project.roles.sales[0]) {
                this.loadUserInfo(project.roles.sales[0])
                return this.lookup.users[project.roles.sales[0]].phone
              } else return null
            },
          },
          'salesperson1-email': {
            description: 'Email of the first salesperson assigned to the project',
            replacer: project => {
              if (project.roles && project.roles.sales && project.roles.sales[0]) {
                this.loadUserInfo(project.roles.sales[0])
                return this.lookup.users[project.roles.sales[0]].email
              } else return null
            },
          },
          'salesperson1-calendar': {
            description: 'Calendar URL of the first salesperson assigned to the project',
            replacer: project => {
              if (project.roles && project.roles.sales && project.roles.sales[0]) {
                this.loadUserInfo(project.roles.sales[0])
                return this.lookup.users[project.roles.sales[0]].calendarUrl
              } else return null
            },
          },
          trackingnumber1: {
            description: 'The first tracking number added to the project',
            replacer: project => {
              if (project.shipments && project.shipments[0]) return project.shipments[0].tracking_number
              else return null
            },
          },
          'trackingnumber1-url': {
            description: 'URL to track the first tracking number added to the project',
            replacer: project => {
              if (project.shipments && project.shipments[0]) return project.shipments[0].tracking_url_provider
              else return null
            },
          },
          'trackingnumber1-provider': {
            description: 'Shipping provider of the first tracking number added to the project',
            replacer: project => {
              if (project.shipments && project.shipments[0] && project.shipments[0].rate_details) {
                return project.shipments[0].rate_details.provider
              } else return null
            },
          },
          'po-number': {
            description: 'The number of a Purchase Order submitted at checkout',
            replacer: project => project.checkout ? project.checkout.po : null,
          },
          'checkout-po-email': {
            description: 'Accounts payable email from checkout (if available)',
            replacer: project => project.checkout ? (project.checkout.email || '') : '',
          },
          'checkout-po-phone': {
            description: 'Accounts payable phone from checkout (if available)',
            replacer: project => project.checkout ? (project.checkout.phone || '') : '',
          },
          'checkout-po-name': {
            description: 'Accounts payable name from checkout (if available)',
            replacer: project => project.checkout ? (project.checkout.name || '') : '',
          },
          'checkout-billing-name': {
            description: 'Bill-to name from checkout (if available)',
            replacer: project => project.checkout ? ((project.checkout.billing || {}).name || '') : '',
          },
          'checkout-billing-first-name': {
            description: 'The first word of the bill-to name from checkout (if available)',
            replacer: project => project.checkout ? ((project.checkout.billing || {}).name || '').trim().split(' ')[0] : '',
          },
          'checkout-billing-company': {
            description: 'Bill-to company from checkout (if available)',
            replacer: project => project.checkout ? ((project.checkout.billing || {}).company || '') : '',
          },
          'checkout-billing-phone': {
            description: 'Bill-to phone from checkout (if available)',
            replacer: project => project.checkout ? ((project.checkout.billing || {}).phone || '') : '',
          },
          'quote-rejection-reason': {
            description: 'Quote Rejection Reason (if available)',
            replacer: project => project.quoteRejectionReason || 'Unspecified',
          },
          'chat-subscriber-name': {
            description: 'Name of customer that is receiving a chat email notification',
            replacer: data => data.chatSubscriberName || '',
          },
          'chat-subscriber-topic': {
            description: 'Topic of the email notification a customer is getting for a new message',
            replacer: data => data.chatSubscriberTopic || '',
          },
          'chat-subscriber-messages': {
            description: 'Preview of the messages an email notification is getting sent for',
            replacer: data => {
              const messages = (data.chatSubscriberMessages || []).sort((a, b) => a - b)
              const namesToGet = [...new Set(messages.map(x => x.sender).filter(x => x))]
              namesToGet.forEach(n => this.loadUserInfo(n))
              const nameLookup = {}
              namesToGet.forEach((n, i) => { nameLookup[n] = this.lookup.users[n].fullName })
              const messageHTML = messages.map(m => {
                let string = ''
                string += `<p><u><strong>${((m.sender ? nameLookup[m.sender] : m.anonymousUserName) || 'Someone')}</strong> at ${new Date(m.created).toLocaleString()}:</u></p>`
                string += `<div>${m.content}</div>`
                return string
              }).join('<br><br>')
              return messageHTML
            },
          },
          'chat-subscriber-link': {
            description: 'URL to the chat with the new messages a customer is being notified about',
            replacer: data => data.chatSubscriberLink || document.location.origin,
          },
        },
        lists: [],
        lookup: {
          users: {},
          chatRooms: {},
          zohoContacts: {},
          organizations: {},
          accounts: {},
          jobs: {},
        },
        state: {
          activeProject: null,
          skipChange: {
            userProfile: false,
            settingsOpen: false,
          },
          models: {},
          uploadingProfilePic: false,
          settingsOpen: false,
        },
        subscriptions: {},
        darkModePreference: false,
        modelExtensions: MODEL_EXTENSIONS,
        allTeams: [],
        staff: [],
        showTaskReader: false,
        readerTask: null,
        readerElement: null,
        totalReaderTasks: 0,
        enterpriseOverride: window.localStorage.getItem('enterpriseOverride'),
        domainEnterprise: { text: 'Default', value: null, enterpriseID: null },
        localRecentProject: null,
        cartReplacementPromise: null,
        cachedStripeTaxCodes: null,
        cachedStripeTaxCodesRetrieved: null,
        cachedStripeTaxCodesPromise: null,
        forceEnterpriseOverrideSelection: false,
        tasksThatNeedOpening: [],
        materialProperties: [
          {
            text: 'Economical',
            value: 'cost',
            color: 'green',
            icon: 'fas fa-dollar-sign',
            min: 120,
            max: 20,
          },
          {
            text: 'Turnaround Speed',
            value: 'turnaround',
            color: 'blue',
            icon: 'fas fa-shipping-fast',
          },
          {
            text: 'Flexibility',
            value: 'flexibility',
            color: 'pink',
            icon: 'fas fa-rainbow',
            min: 4.5,
            max: 0,
          },
          {
            text: 'Strength',
            value: 'strength',
            color: 'amber',
            icon: 'fas fa-dumbbell',
          },
          {
            text: 'Heat Resistance',
            value: 'temperatureResistance',
            color: 'red',
            icon: 'fas fa-temperature-high',
            min: 20,
            max: 208,
          },
        ],
        printerStates: {
          C: { text: 'Updating Configuration', color: '#AB47BC' },
          I: { text: 'Idle', color: 'dodgerblue' },
          B: { text: 'Busy', color: '#FFC107' },
          P: { text: 'Printing', color: 'limegreen' },
          D: { text: 'Pausing', color: '#CDDC39' },
          S: { text: 'Paused', color: '#FF9800' },
          R: { text: 'Resuming', color: '#CDDC39' },
          H: { text: 'Stopped', color: 'red' },
          F: { text: 'Flashing', color: '#9C27B0' },
          T: { text: 'Changing Tool', color: '#009688' },
          X: { text: 'Disconnected', color: '#F44336' },
          E: { text: 'Error', color: '#F44336' },
          U: { text: 'Unknown', color: '#607D8B' },
          O: { text: 'Offline', color: '#9E9E9E' },
        },
      }
    },
    computed: {
      // Per-enterprise: cap stage counters at model quantity. The single place the raw
      // config/workflows field is read; coerced to a concrete boolean here so an absent
      // field or an un-loaded doc means UNCAPPED (the default) rather than undefined.
      capStageCounts () {
        return !!(this.workflowsConfig || {}).capStageCountsAtQuantity
      },
      publicTaskItems: function () {
        return this.$root.publicTasks.map(x => {
          return {
            text: this.$root.taskName(x) === 'formfactories' ? 'Home Page' : this.$root.taskName(x),
            value: x.uuid,
          }
        })
      },
      canUseEmailEditor () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.emailEditorTeams || [])
      },
      // Access to Settings > Emails (the templates/automations page). Deliberately a
      // separate key from emailEditorTeams above, which grants a different capability
      // (composing an email to a client from a project) and must not be widened here.
      canEditEmailTemplates () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.emailTemplateEditorTeams || [])
      },
      canSeePricing () {
        if (!this.enableProjectPricing) return false
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.pricingTeams || [])
      },
      canEditMachines () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.machineEditorTeams || [])
      },
      canCreateTaskCategories () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.taskCategoryCreators || [])
      },
      canEditCatalog () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.catalogEditors || [])
      },
      canEditStoreProducts () {
        if (!this.$root.configPublic.enableStore) return false
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.storeProductEditors || [])
      },
      canViewClientList () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.dataExporterTeams || [])
      },
      canViewProjectMetrics () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.metricsAccessTeams || [])
      },
      canViewBusinessMetrics() {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.metricsAccessBusinessTeams);
      },
      canViewProductionMetrics() {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.metricsAccessProductionTeams);
      },
      canViewMachinesMetrics() {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.metricsAccessMachinesTeams);
      },
      canSetSchedules () {
        if (!this.isStaff) return false
        if (this.isExecutive) return true
        return this.isInTeams(this.configPublic?.scheduleEditorTeams || [])
      },
      /**
       * Whether this enterprise may INVITE schedule-only members.
       *
       * Explicit opt-in with NO grandfather default, unlike the schedule module
       * toggles. Production's `acceptStaffInvite` predates this role: it blind-sets
       * `staff/{uid}.access` from the invite and ignores `scheduleOnly`, so a
       * schedule-only invite accepted against production creates a REAL STAFF DOC —
       * and the deployed rules gate "is staff" on that document existing. Until the
       * hardened backend (formfactories-functions 7af7701, currently develop-only)
       * is on master, this switch is the thing standing between the button and a
       * privilege escalation. Absent MUST mean off.
       *
       * It gates provisioning only. Recognising an existing schedule-only member
       * (isScheduleOnly) and containing them is unconditional, because Vue 3 can
       * already provision this role regardless of what Vue 2 shows.
       */
      scheduleOnlyRoleEnabled () {
        return this.configPublic?.enableScheduleOnlyRole === true
      },
      /** Who may hand out a schedule-only invitation: the same people who manage schedules. */
      canInviteScheduleOnly () {
        return !!(this.scheduleOnlyRoleEnabled && this.canSetSchedules)
      },
      maxMemorySample: function () {
        return this.memoryHistory.length ? this.memoryHistory[this.memoryHistory.length - 1].jsHeapSizeLimit : 1
      },
      showMemory: {
        get: function () {
          return !!this.$root.userProfile?.prefs_showMemory && window.performance.memory
        },
        set: function (value) {
          this.$set(this.$root.userProfile, 'prefs_showMemory', value)
        },
      },
      defaultExtensions: function () {
        return {
          models: ['stl', 'obj', 'step', 'stp', 'sldprt', 'asm', 'f3d', 'fbx', 'iam', 'ipt', 'neu', 'prt', 'sldasm', 'smb', 'smt', 'stpz', 'wire', 'x_b', 'x_t', 'dxf', 'cdr', 'ai', 'svg'],
          jobs: [...new Set(this.allMachines.flatMap(m => (m.extension || '').split(',').map(x => x.toLowerCase().replace('.', '').trim()).filter(x => x)))],
          linked: ['factory', '3mf', 'png', 'jpeg', 'jpg'],
        }
      },
      defaultCustomerExtensions: function () {
        return {
          models: ['stl', 'obj', 'step', 'stp', 'sldprt', 'asm', 'f3d', 'fbx', 'iam', 'ipt', 'neu', 'prt', 'sldasm', 'smb', 'smt', 'stpz', 'wire', 'x_b', 'x_t', 'dxf', 'cdr', 'ai', 'svg'],
          jobs: [],
          linked: [],
        }
      },
      extensions: function () {
        const extensions = this.defaultExtensions
        const overrides = this.configPublic?.fileTypeBehavior || []
        overrides.forEach(o => {
          const ext = o.extension.toLowerCase().replace('.', '').trim()
          Object.keys(extensions).forEach(k => {
            if (extensions[k].includes(ext)) extensions[k] = extensions[k].filter(x => x !== ext)
          })
          if (extensions[o.behavior]) extensions[o.behavior].push(ext)
        })

        return extensions
      },
      customerExtensions: function () {
        const extensions = this.defaultCustomerExtensions
        const overrides = this.configPublic?.fileTypeBehavior || []
        overrides.forEach(o => {
          const ext = o.extension.toLowerCase().replace('.', '').trim()
          Object.keys(extensions).forEach(k => {
            if (extensions[k].includes(ext)) extensions[k] = extensions[k].filter(x => x !== ext)
          })
          if (extensions[o.customerBehavior]) extensions[o.customerBehavior].push(ext)
        })

        return extensions
      },
      formfactoriesPlan: function () {
        const currentPlan = this.allTiers.find(x => x.id === this.$root.enterprise.tier)
        if (!currentPlan) return null
        const overridePlan = (this.enterprise.customPlanPricing || {})[this.enterprise.tier] || {}
        if (overridePlan.price === '' || isNaN(overridePlan.price)) delete overridePlan.price
        return Object.assign({}, currentPlan, overridePlan)
      },
      formfactoriesPlanPrice: function () {
        if (!this.formfactoriesPlan) return null
        return +this.formfactoriesPlan.price
      },
      favicon: function () {
        if (this.isStaff) return this.brand.staffFavicon
        else return this.brand.userFavicon
      },
      showInviteDialog: {
        get: function () {
          return !!(this.userID && this.staffInviteID && !this.isStaff)
        },
        set: function (value) {
          if (!value) {
            this.staffInviteID = null
          }
        },
      },
      stripeCredentials: function () {
        if (!this.enterprise.stripeConnectAccountID) return
        return { stripeAccount: this.enterprise.stripeConnectAccountID }
      },
      allMachineInstancesByID: function () {
        const result = {}
        this.allMachineInstances.forEach(x => { result[x.id] = x })
        return result
      },
      userID: function () {
        return this.currentUser ? this.currentUser.uid : null
      },
      userTasks: function () {
        // Return all tasks in userTaskGroups, but filter out duplicates (by uuid)
        if (this.userTaskGroups.length === 0) return []
        if (this.userTaskGroups.length === 1) return this.userTaskGroups[0]

        const tasks = []
        this.userTaskGroups.forEach(g => {
          g.forEach(t => {
            if (!tasks.find(x => x.uuid === t.uuid)) tasks.push(t)
          })
        })
        return tasks
      },
      taskReaderTeams: function () {
        return this.simulateTeams ? this.simulatedTeams : this.myTeams.map(x => x.uuid)
      },
      tier: function () {
        return this.allTiers.find(t => !this.enterprise.tier || t.id === this.enterprise.tier) || { features: {} }
      },
      enableProjectPricing: function () {
        return this.tier.features.shopSupport && this.configPublic.enableProjectPricing
      },
      enablePublicChats: function () {
        return this.tier.features.publicChatRooms && this.configPublic.enablePublicChats
      },
      enablePrintQueue: function () {
        if (!this.$root.isStaff) return false
        if (!this.tier.features.printQueue) return false
        return this.configPublic.enablePrintQueue
      },
      enableTaskReader: function () {
        if (!this.tier.features.reader) return false
        if (!this.enterprise.enableTaskReader) return false
        return (!this.$root.enterprise.enableTaskReaderBetaOnly || this.betaMode)
      },
      enableShelves: function () {
        if (!this.$root.isStaff) return false
        if (!this.tier.features.shelves) return false
        if (!this.$root.enterprise.enableShelves) return false
        return (!this.$root.enterprise.enableShelvesBetaOnly || this.betaMode)
      },
      enableAutomatedPricing: function () {
        return this.enableProjectPricing && this.tier.features.automatedPricing
      },
      enableInventory: function () {
        if (!this.$root.isStaff) return false
        if (!this.tier.features.inventory) return false
        if (!this.$root.enterprise.enableInventory) return false
        return (!this.$root.enterprise.enableInventoryBetaOnly || this.betaMode)
      },
      /** The staff scheduler is on for this enterprise (Beta setting on config/public). */
      scheduleEnabled: function () {
        return resolveScheduleEnabled(this.enterpriseID, this.configPublic)
      },
      enableSchedule: function () {
        if (!this.$root.isStaff) return false
        return this.scheduleEnabled
      },
      /**
       * A schedule-only member whose enterprise has the scheduler off, known for
       * sure (identity and config/public both loaded). They have nowhere to be.
       */
      scheduleOnlyLockedOut: function () {
        return !!(this.isScheduleOnly && !this.scheduleOnlyPending && this.configPublicLoaded && !this.scheduleEnabled)
      },
      // Staff-facing tech support / ticketing. Available to STAFF of every enterprise (not
      // enterprise end-customers). Devs are always staff everywhere, so they see it too; the
      // /dev dashboard itself remains separately gated via isDev.
      enableTechSupport: function () {
        return this.$root.isStaff
      },
      betaMode: function () {
        return this.configPublic.enableStaffBetaOptIn && this.userProfile && this.userProfile.prefs_beta
      },
      staffEnterpriseOptions: function () {
        if (Capacitor.isNative) return this.staffEnterprises
        return [this.domainEnterprise].concat(this.staffEnterprises)
      },
      staffEnterpriseIDs: function () {
        const tokenEnterpriseIDs = Object.keys(((this.userToken || {}).claims || {}).enterprises || {}).filter(e => this.userToken.claims.enterprises[e].staff)
        if ((this.currentEnterpriseStaffAccess || 0) >= 5 && this.enterpriseID) tokenEnterpriseIDs.push(this.enterpriseID)
        return Array.from(new Set(tokenEnterpriseIDs))
      },
      staffEnterprises: function () {
        return this.staffEnterpriseIDs.map(id => ({
          text: this.staffEnterpriseNames[id] || 'Loading...',
          value: id,
          url: this.staffEnterpriseURLs[id] || '',
        }))
      },
      staffAccessPending: function () {
        return !!(this.currentUser && !this.currentEnterpriseStaffAccessLoaded)
      },
      /**
       * True when this browser should be handed to the Vue 3 app without being asked: resolved
       * staff of an enterprise whose owner turned on `enableNewAppForStaff` (Settings >
       * Features), with no ff_ui choice recorded. Held until `config/public` has loaded, so
       * the setting is read rather than its absence, and read once per page load: turning it
       * on moves staff the next time they open the app, not out of the page they are on.
       * The two cookie reads are not reactive, and do not need to be: the only in-page cookie
       * changes are logout clearing it and the switch-over setting it, and `loggingOut` /
       * `currentUser` change around the first (so this recomputes) while the second leaves the page.
       *
       * Staff is NOT `isStaff`. That computed is also true for every developer UID and falls
       * back to token claims, while the Vue 3 app counts only the admin token claim (strictly
       * `true`) or this enterprise's staff DOCUMENT at access >= 5. A developer with no staff
       * document is a customer there: its guard (useUiVersionGuard) clears the cookie and
       * reloads, this app would push again, and the two would loop. So the test here is Vue 3's.
       *
       * Likewise the enterprise: Vue 3 resolves it from the host alone, so an enterprise
       * override must not count. `domainEnterprise.enterpriseID` is null until the host
       * resolution lands, which holds the push back until then.
       */
      vue3AutoOptInDue: function () {
        const hostEnterpriseID = this.domainEnterprise.enterpriseID
        return shouldAutoOptIn({
          enterpriseOptedIn: this.newAppForStaffAtLoad === true,
          enterpriseConfigPending: this.newAppForStaffAtLoad === null,
          activeEnterpriseIsHosts: !!hostEnterpriseID && hostEnterpriseID === this.enterpriseID,
          signedIn: !!this.currentUser && !this.loggingOut,
          staffAccessPending: this.staffAccessPending,
          isStaff: this.userToken?.claims?.admin === true || (this.currentEnterpriseStaffAccess || 0) >= 5,
          isNative: Capacitor.isNativePlatform(),
          hasVue3Cookie: hasVue3Cookie(),
          hasLegacyCookie: hasLegacyCookie(),
          // `$route` is reactive, so opening /alerts in-app recomputes this and the watcher
          // below switches; until the first navigation resolves it is START (no name).
          onNewAppOnlyRoute: NEW_APP_ONLY_ROUTES.includes(this.$route.name),
        })
      },
      staffIdleTimeoutMinutes: function () {
        const minutes = Number(this.configPublic?.staffIdleTimeoutMinutes)
        return Number.isFinite(minutes) && minutes > 0 ? minutes : 0
      },
      staffIdleTimeoutStorageKey: function () {
        if (!this.enterpriseID || !this.currentUser?.uid) return null
        return `staffIdle:lastActivity:${this.enterpriseID}:${this.currentUser.uid}`
      },
      staffIdleTimeoutUploadsActive: function () {
        const uploadState = this.$store?.state?.upload?.uploads || []
        return this.activeUploads > 0 || uploadState.some(u => ['queued', 'uploading'].includes(u.status))
      },
      staffIdleTimeoutEnabled: function () {
        if (!this.currentUser?.uid) return false
        if (Capacitor.isNativePlatform()) return false
        if (this.staffAccessPending) return false
        if (!this.isStaff) return false
        if (!this.configPublic?.enableStaffIdleTimeout) return false
        if (!this.staffIdleTimeoutMinutes) return false
        return !STAFF_IDLE_TIMEOUT_IMMUNE_USER_IDS.includes(this.currentUser.uid)
      },
      claims: function () {
        const legacyClaims = (this.userToken || {}).claims || {}
        const enterpriseClaims = (legacyClaims.enterprises || {})[this.enterpriseID]
        if (this.staffAccessPending) {
          return {
            access: 0,
            staff: false,
            admin: false,
          }
        }
        if (this.currentEnterpriseStaffAccess !== null) {
          const explicitAccess = +this.currentEnterpriseStaffAccess
          return {
            access: explicitAccess,
            staff: explicitAccess >= 5,
            admin: explicitAccess >= 9,
          }
        }
        return enterpriseClaims || legacyClaims
      },
      enterprise: function () {
        return Object.assign(this.enterpriseRaw || {}, this.configPublic || {})
      },
      meilisearchKey: function () {
        return this.management?.meilisearchKey;
      },
      darkMode: function () {
        return this.darkModePreference && this.isStaff
      },
      secondaryCardColor: function () {
        const color = this.theme.cardColor || '#FFFFFF'
        const luminance = (0.299 * parseInt(color.substr(1, 2), 16) + 0.587 * parseInt(color.substr(3, 2), 16) + 0.114 * parseInt(color.substr(5, 2), 16)) / 255
        const backgroundColor = this.theme.backgroundColor
        const backgroundLuminance = (0.299 * parseInt(backgroundColor.substr(1, 2), 16) + 0.587 * parseInt(backgroundColor.substr(3, 2), 16) + 0.114 * parseInt(backgroundColor.substr(5, 2), 16)) / 255
        const lumDif = luminance - backgroundLuminance
        return this.blendColors(this.theme.cardColor, this.theme.backgroundColor, 0.010 / lumDif)
      },
      brandEmail: function () {
        if (this.brand && this.brand.email) return this.brand.email
        else return this.enterprise.email
      },
      brandName: function () {
        if (this.brand && this.brand.name && !this.brand.hideEnterpriseAttribution) return this.brand.name
        else return this.enterprise.name
      },
      brandPhone: function () {
        if (this.brand && this.brand.phone) return this.brand.phone
        else return this.enterprise.phone
      },
      showHeader: function () {
        if (this.$route && this.$route.meta && this.$route.meta.hideHeader) return false
        return !(this.$route.name === 'Counting' && this.hideHeaders)
      },
      publicBrands: function () {
        return this.enterprise.brands.filter(b => b.public)
      },
      theme: function () {
        return this.darkMode ? this.darkTheme : this.lightTheme
      },
      darkTheme: function () {
        return this.isStaff ? this.brand.themes.staffDark : this.brand.themes.userDark
      },
      lightTheme: function () {
        return this.isStaff ? this.brand.themes.staffLight : this.brand.themes.userLight
      },
      timezone() {
        return this.management?.productionSchedule?.timezone || 'America/Los_Angeles';
      },
      brandProperties: function () {
        return {
          '--highlightColor': this.theme.color,
          '--headerColor': this.theme.headerColor,
          '--paneColor1': this.theme.cardColor,
          '--paneColor2': this.secondaryCardColor,
          '--cardColorDark': this.darkTheme.cardColor,
          '--cardColorLight': this.lightTheme.cardColor,
          '--headerTextColor': 'white',
        }
      },
      lightHeaderText: function () {
        const headerColor = this.theme.headerColor || '#FFFFFF'
        const luminance = (0.299 * parseInt(headerColor.substr(1, 2), 16) + 0.587 * parseInt(headerColor.substr(3, 2), 16) + 0.114 * parseInt(headerColor.substr(5, 2), 16)) / 255
        return luminance < 0.5
      },
      brand: function () {
        // Match user's brand preference if available
        if (this.isStaff && this.userProfile && this.userProfile.prefs_brand) {
          const themeMatch = this.enterprise.brands.find(b => b.id === this.userProfile.prefs_brand && b.public)
          if (themeMatch) return themeMatch
        }

        // Otherwise match user's current domain with matching enterprise domain
        let domainMatch
        const hostName = window.location.hostname.toLowerCase().trim()
        domainMatch = this.enterprise.domains.find(d => d.domain.toLowerCase().trim() === hostName)
        if (!domainMatch) {
          const fullHostName = window.location.hostname.split('.').slice(-2).join('.').toLowerCase().trim()
          domainMatch = this.enterprise.domains.find(d => d.domain.toLowerCase().trim() === fullHostName)
        }

        // Get any brand associated with the domain
        if (!domainMatch || !domainMatch.brand || !this.enterprise.brands.find(b => b.id === domainMatch.brand)) return this.enterprise.brands.find(b => b.default)
        else return this.enterprise.brands.find(b => b.id === domainMatch.brand)
      },
      tasksLeft: function () {
        if (!this.currentUser) return 0
        const ids = [this.currentUser.uid].concat(this.myTeams.map(x => x.uuid))
        return this.userTasks
          .filter(t => (t.members || []).includes(this.currentUser.uid))
          .map(t => new Set(ids.flatMap(i => t.todoIDsByUser[i] || [])).size)
          .reduce((a, b) => a + b, 0)
      },
      publicTasks: function () {
        const tasks = this.publishedTasks
        const materials = (((this.$root.catalog || []).find(x => x.name === 'Material') || {}).children || []).filter(x => !x.advanced && x.active)
        const materialTasks = materials.map(mat => this.materialToTask(mat))
        return tasks.concat(materialTasks)
      },
      materialIDToPathMap: function () {
        const materials = ((this.catalog || []).find(x => x.name === 'Material') || {}).children || []
        const pathIDs = {}
        materials.forEach(mat => {
          const newPath = encodeURIComponent(mat.name.toLowerCase().replace(/ /g, '-'))
          if (!pathIDs[newPath]) pathIDs[newPath] = []
          pathIDs[newPath].push(mat.uuid)
        })
        const result = {}
        Object.keys(pathIDs).forEach(path => {
          pathIDs[path].forEach((id, i) => {
            if (i === 0) result[pathIDs[path][i]] = path
            else result[pathIDs[path][i]] = `${path}-${i + 1}`
          })
        })
        return result
      },
      materialPathToIDMap: function () {
        const result = {}
        Object.keys(this.materialIDToPathMap).forEach(k => {
          result[this.materialIDToPathMap[k]] = k
        })
        return result
      },
      publicTaskIDToPathMap: function () {
        // Block off paths used by Lana
        const pathIDs = {}
        const routeBlacklist = [
          '404',
          'login',
          'dashboard',
          'tasks',
          'task',
          'chat',
          'scanner',
          'reader',
          'projects',
          'info',
          'materials',
          'submit',
          'counting',
          'playground',
          'landing',
          'inventory',
          'queue',
          'customers',
          'company',
          'organizations',
          'project-list',
          'flagged-messages',
          'staff',
          'eula',
          'privacypolicy',
          'termsandconditions',
        ]
        routeBlacklist.forEach(route => { pathIDs[route] = [null] })

        // Generate path for every published task
        this.publishedTasks.forEach(task => {
          const newPath = encodeURIComponent(this.taskName(task).toLowerCase().replace(/ /g, '-'))
          if (!pathIDs[newPath]) pathIDs[newPath] = []
          pathIDs[newPath].push(task.uuid)
        })

        // Handle path conflicts
        const result = {}
        Object.keys(pathIDs).forEach(path => {
          pathIDs[path].forEach((id, i) => {
            if (id) {
              if (i === 0) result[pathIDs[path][i]] = path
              else result[pathIDs[path][i]] = `${path}-${i + 1}`
            }
          })
        })
        return result
      },
      publicTaskPathToIDMap: function () {
        const result = {}
        Object.keys(this.publicTaskIDToPathMap).forEach(k => {
          result[this.publicTaskIDToPathMap[k]] = k
        })
        return result
      },
      recentAccountProject: function () {
        if (this.userProfile && this.userProfile.recentProjects) {
          return this.userProfile.recentProjects[this.userProfile.recentProjects.length - 1]
        } else return null
      },
      projectPreview: function () {
        return this.localRecentProject || this.recentAccountProject || this.localRecentProject
      },
      myPermissions: function () {
        return Array.from(new Set(this.myTeams.flatMap(x => x.permissions || [])))
      },
      listOrder: function () {
        return this.$root.management ? (this.$root.userProfile && this.$root.userProfile.prefs_showDrafts ? this.$root.management.listOrder : this.$root.management.listOrder.filter(x => x !== 5)) : null
      },
      locationView: {
        get: function () {
          if (this.userProfile && (this.userProfile.prefs_location === '' || this.allLocations.find(x => x.id === this.userProfile.prefs_location))) return this.userProfile.prefs_location
          else return this.allLocations[0] ? this.allLocations[0].id : ''
        },
        set: function (val) {
          if (this.userProfile) this.$set(this.userProfile, 'prefs_location', val)
        },
      },
      allLocations: function () {
        return this.enterprise.locations.filter(x => x.active) || [this.enterprise.defaultLocation]
      },
      locations: function () {
        const allMyLocations = Array.from(new Set(this.myTeams.flatMap(x => x.locations || [this.enterprise.defaultLocation]).filter(x => x)))
        return this.allLocations.filter(x => allMyLocations.includes(x.id))
      },
      locationViews: function () {
        return [{ id: '', name: 'Show All' }].concat(this.allLocations)
      },
      holiday: function () {
        if (this.$root.userProfile && this.$root.userProfile.prefs_disableHolidayExpiration > Date.now()) return null
        const day = new Date().getDate()
        const month = new Date().getMonth() + 1
        if (month === 12 && day <= 25) return 'christmas'
        else if ((month === 12 && day >= 31) || (month === 1 && day <= 1)) return 'newyears'
        else if (month === 7 && day <= 4) return 'july4'
        // else if (month === 4 && (day === 1 || day === 21) && this.$root.enterpriseID === 't9WXKqgdGpfQdxN54DKi') return 'aprilfools'
        // else if (month === 9 && day === 14) return 'leogreencard'
        else return null
      },
      debug: function () {
        return this.currentUser && this.userProfile && this.userProfile.prefs_debug
      },
      machineTags: function () {
        if (this.management && this.management.machineTags) return this.management.machineTags.filter(x => x.active !== false)
        else return []
      },
      myTeams: function () {
        return this.allTeams.filter(x => x.uuid === 'public' || x.members.includes(this.userID))
      },
      unreadNotifications: function () {
        return this.userNotifications.filter((n) => !n.read).length
      },
      testMode: function () {
        return this.currentUser && this.userProfile && this.userProfile.testMode
      },
      taskLists: function () {
        const lists = JSON.parse(JSON.stringify(this.enterprise.taskLists || []))
        if (!this.$root.configPublic.enableMaterialTasks) return lists

        const allTechs = this.$root.catalog?.find(x => x.name === 'Technology')?.children || []
        const techs = allTechs.filter(t => {
          if (t.advanced) return false
          if (!t.active) return false
          const materials = this.$root.catalog.find(x => x.name === 'Material').children
            .filter(c => !c.restrictions.Technology.length || c.restrictions.Technology.includes(t.name))
            .filter(c => c.active &&  !c.advanced)
          return !!materials.length
        })

        const techLists = techs.map(tech => ({ name: tech.displayName, uuid: tech.uuid }))
        const materialsList = lists.find(x => x.uuid === this.$root.configPublic.materialTasksList)
        if (materialsList) {
          if (!materialsList.megaMenuSubcategories) materialsList.megaMenuSubcategories = []
          if (!materialsList.megaMenuSubcategorySortOrders) materialsList.megaMenuSubcategorySortOrders = {}
          materialsList.megaMenuSubcategories.push(...techs.map(x => x.uuid))
          techs.forEach((tech, t) => { materialsList.megaMenuSubcategorySortOrders[tech.uuid] = t })
        }
        return lists.concat(techLists)
      },
      developerUIDs: function () {
        // The built-in config list only bootstraps access. As soon as the database-backed
        // `developers` collection has any entries, it becomes the single source of truth
        // (so removing a migrated UID via the dashboard actually sticks).
        const dbUIDs = this.developerList.map(d => d.uid).filter(Boolean)
        if (dbUIDs.length) return dbUIDs
        return DEVELOPER_USER_IDS
      },
      builtInDeveloperUIDs: function () {
        return DEVELOPER_USER_IDS
      },
      isDev: function () {
        if (!this.currentUser) return false
        return this.developerUIDs.includes(this.currentUser.uid)
      },
      isAlumni: function () {
        if (this.$root.userToken?.claims?.admin) return true
        if (this.isDev) return true
        return this.claims.access >= 4
      },
      /**
       * True until BOTH identity reads resolve, so nothing flashes on boot.
       * Staff access is part of the condition because isScheduleOnly is defined
       * as "member AND NOT staff": acting on it before the staff read lands
       * could contain a real staff member for a moment.
       */
      scheduleOnlyPending: function () {
        return !!(this.currentUser && (!this.currentEnterpriseScheduleMemberLoaded || this.staffAccessPending))
      },
      /**
       * The station-only tier: a person with a `schedulerOnly` scheduleEmployees row
       * and NO staff document.
       *
       * Deliberately NOT folded into `isAlumni`. That computed reads
       * `claims.access >= 4` and is used across the nav, header and account pages to
       * WIDEN what a user sees; this role exists precisely to grant almost nothing.
       * The two must never be conflated.
       *
       * `!isStaff` is a conjunct, not an assumption: a person can hold both a staff
       * doc and a schedule row (they were invited to the schedule, then hired), and
       * staff always wins. A staff member is never contained.
       */
      isScheduleOnly: function () {
        if (!this.currentUser) return false
        // Wait for the staff read: while it is pending, `claims` is forced to
        // access 0 and isStaff reads false, so a real STAFF member who also has
        // a schedule row would flash schedule-only and could be contained by it.
        if (this.staffAccessPending) return false
        if (this.isStaff || this.isAlumni) return false
        const member = this.currentEnterpriseScheduleMember
        return !!(member && member.schedulerOnly === true && member.active !== false)
      },
      isStaff: function () {
        if (this.$root.userToken?.claims?.admin) return true
        if (this.isDev) return true
        return this.claims.access >= 5
      },
      isExecutive: function () {
        if (this.$root.userToken?.claims?.admin) return true
        if (this.isDev && this.superUserEnabled) return true
        return this.claims.access >= 8
      },
      isAdmin: function () {
        if (this.$root.userToken?.claims?.admin) return true
        if (this.isDev && this.superUserEnabled) return true
        return this.claims.access >= 9
      },
      // NOTE: we intentionally do NOT filter out developerUIDs here. `this.staff` only ever
      // contains genuine members of the CURRENT enterprise (super-user access is a client-side
      // role override and never writes a staff doc), so filtering devs out only ever hid them
      // on the enterprise where they're a real employee — which broke @mentions/tagging of them.
      staffAndAlumniList: function () {
        return Object.keys(this.staff).filter(x => this.staff[x].access >= 4).map(uid => ({ value: uid, text: this.lookup.users[uid].displayName || this.lookup.users[uid].fullName, icon: this.lookup.users[uid].profilePic, email: this.lookup.users[uid].email, fullName: this.lookup.users[uid].fullName || this.lookup.users[uid].displayName }))
      },
      staffList: function () {
        return Object.keys(this.staff).filter(x => this.staff[x].access >= 5).map(uid => ({ value: uid, text: this.lookup.users[uid].displayName || this.lookup.users[uid].fullName, icon: this.lookup.users[uid].profilePic, email: this.lookup.users[uid].email, fullName: this.lookup.users[uid].fullName || this.lookup.users[uid].displayName }))
      },
      staffAndTeamList: function () {
        const teams = this.$root.allTeams.map(x => ({ text: x.name, value: x.uuid, isTeam: true }))
        return this.$root.staffList.concat(teams)
      },
      catalog: function () {
        return this.production.catalog || []
      },
      catalogCompatibilityMap: function () {
        const map = {}
        this.catalog.forEach(optionA => {
          optionA.children.forEach(childA => {
            this.catalog.forEach(optionB => {
              optionB.children.forEach(childB => {
                // Determine compatibility between optionA/childA and optionB/childB
                const childARestrictions = (childA.restrictions || {})[optionB.name]
                const childBRestrictions = (childB.restrictions || {})[optionA.name]
                const childAAcceptsChildB = !childARestrictions || childARestrictions.includes(childB.name)
                const childBAcceptsChildA = !childBRestrictions || childBRestrictions.includes(childA.name)
                const compatible = childAAcceptsChildB && childBAcceptsChildA

                // Add to map
                if (!map[optionA.name]) map[optionA.name] = {}
                if (!map[optionA.name][childA.name]) map[optionA.name][childA.name] = {}
                if (!map[optionA.name][childA.name][optionB.name]) map[optionA.name][childA.name][optionB.name] = {}
                if (!map[optionA.name][childA.name][optionB.name][childB.name]) map[optionA.name][childA.name][optionB.name][childB.name] = {}

                if (!map[optionB.name]) map[optionB.name] = {}
                if (!map[optionB.name][childB.name]) map[optionB.name][childB.name] = {}
                if (!map[optionB.name][childB.name][optionA.name]) map[optionB.name][childB.name][optionA.name] = {}
                if (!map[optionB.name][childB.name][optionA.name][childA.name]) map[optionB.name][childB.name][optionA.name][childA.name] = {}

                map[optionA.name][childA.name][optionB.name][childB.name] = compatible
                map[optionB.name][childB.name][optionA.name][childA.name] = compatible
              })
            })
          })
        })
        return map
      },
      allMachines: function () {
        if (!this.management || !this.management.machines) return []
        const machines = this.management.machines
        machines.forEach(machineType => {
          for (let i = 0; i < machineType.instances.length; i++) {
            const uuid = machineType.instances[i].uuid
            if (this.allMachineInstancesByID[uuid]) {
              machineType.instances[i] = this.allMachineInstancesByID[uuid]
            }
          }
        })
        return machines
      },
      machines: function () {
        if (this.locationView) {
          return this.allMachines.map((machine, m) => {
            const newMachine = Object.assign({}, machine)
            newMachine.instances = this.allMachines[m].instances.filter(i => this.canShowLocation(i.location))
            return newMachine
          }).filter(x => x.instances.length)
        } else return this.allMachines
      },
      machineAvailability: function () {
        return this.production.machines
      },
      catalogByName: function () {
        const result = Object.assign({}, ...this.catalog.map(x => ({ [x.name]: Object.assign({}, x) })))
        this.catalog.forEach(o => {
          result[o.name].children = Object.assign({}, ...result[o.name].children.map(x => ({ [x.name]: x })))
        })
        return result
      },
    },
    watch: {
      /**
       * The router guard only runs on NAVIGATION, so a member whose identity
       * resolves after boot (or right after accepting an invite) can be sitting
       * on a page the allowlist forbids. This walks them to the schedule the
       * moment the flag flips, and claims any chat rooms their invitation
       * carried - the one moment both the uid and the membership are known.
       */
      /**
       * The router guard signs such a member out on navigation, but config/public
       * can land (or the Beta setting can be switched off) while they sit on the
       * schedule. Same exit as the guard: logout with the explanation query.
       */
      scheduleOnlyLockedOut: function (val) {
        if (val && !this.loggingOut) this.logout({ noScheduleAccess: '1' })
      },
      isScheduleOnly: function (val) {
        if (!val) return
        this.claimInvitedChatRooms()
        const routeName = this.$route && this.$route.name
        if (!SCHEDULE_ONLY_ALLOWED_ROUTES.includes(routeName)) {
          this.$router.push({ name: 'Schedule' }).catch(() => {})
        }
      },
      jobPreviewID: {
        handler: function (val) {
          if (this.jobPreviewListener) this.jobPreviewListener()
          this.jobPreviewData = null
          if (val) {
            this.jobPreviewListener = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'jobs', val),
              snap => {
                this.jobPreviewData = Object.assign({ id: snap.id }, snap.data())
              },
              err => {
                console.log('Error listening to job preview', err)
              },
            )
          }
        },
        immediate: true,
      },
      showMemory: {
        handler: function (val) {
          if (val) {
            this.memoryInterval = setInterval(() => {
              // const current = this.memoryHistory.length ? this.memoryHistory[this.memoryHistory.length - 1].totalJSHeapSize : 0
              const memoryUsage = window.performance.memory
              this.memoryHistory.push(memoryUsage)
              // const updated = memoryUsage.totalJSHeapSize
              // const diff = updated - current
              // if (diff) console.log('DIVISOR', diff / 8312)
              // console.log('diff', updated - current)
              if (this.memoryHistory.length > this.memorySamples) this.memoryHistory.shift()
            }, 100)
          } else {
            clearInterval(this.memoryInterval)
            this.memoryHistory = []
          }
        },
        immediate: true,
      },
      lightHeaderText: {
        handler: function (val) {
          if (Capacitor.isNativePlatform()) StatusBar.setStyle({ style: val ? Style.Dark : Style.Light })
        },
        immediate: true,
      },
      showInviteDialog: {
        handler: function (newVal, oldVal) {
          if (oldVal && !newVal) {
            this.acceptingStaffInvite = false
            // A schedule-only member's navigation belongs to the containment
            // watcher/guard; '/' is never a legal destination for them, and the
            // guard's redirect would reject this push. Guarded either way, since
            // the membership can resolve mid-navigation.
            if (!this.isScheduleOnly) this.$router.push('/').catch(() => {})
          }
        },
        immediate: true,
      },
      viewableTasks: function (val) {
        this.totalReaderTasks = val.length
      },
      taskReaderTeams: {
        handler (val) {
          if (val && val.length) {
            if (this.visibleTaskListener) this.visibleTaskListener()
            this.visibleTaskListener = onSnapshot(
              query(
                collection(this.db, 'enterprises', this.enterpriseID, 'chats'),
                where('readerVisibility', 'array-contains-any', val),
                where('active', '==', true),
              ),
              snap => {
                let result = snap.docs.map(d => Object.assign({ uuid: d.id }, d.data()))
                if (this.configPublic.enableMaterialTasks) {
                  const materialTasks = (((this.catalog || []).find(x => x.name === 'Material') || {}).children || []).filter(x => !x.advanced && x.active).map(mat => this.materialToTask(mat))
                  result = result.concat(materialTasks)
                }
                this.viewableTasks = result
              },
              err => {
                console.log('Error listening to task reader teams', err)
              },
            )
          }
        },
        immediate: true,
      },
      $route: function () {
        this.organizationPreviewOpen = false
        this.recordStaffIdleActivity()
      },
      staffIdleTimeoutEnabled: {
        handler: function (enabled) {
          if (enabled) this.startStaffIdleTimeout()
          else this.stopStaffIdleTimeout()
        },
        immediate: true,
      },
      staffIdleTimeoutMinutes: function () {
        if (this.staffIdleTimeoutEnabled) this.startStaffIdleTimeout()
      },
      staffIdleTimeoutStorageKey: function () {
        this.staffIdleTimeoutEvaluatedStoredSession = false
        if (this.staffIdleTimeoutEnabled) this.startStaffIdleTimeout()
      },
      staffIdleTimeoutUploadsActive: function (active) {
        if (active) this.recordStaffIdleActivity()
      },
      enterpriseOverride: function (val) {
        if (val) window.localStorage.setItem('enterpriseOverride', val)
        else window.localStorage.removeItem('enterpriseOverride')
        const newEnterpriseID = val === null ? this.domainEnterprise.enterpriseID : val
        if (newEnterpriseID !== this.enterpriseID) window.location.reload()
      },
      isStaff: function (newVal, oldVal) {
        if (newVal && !oldVal) this.startStaffSession()
        else if (!newVal && oldVal) {
          if (!this.loggingOut) {
            this.stopStaffSession()
            if ((this.$route.path || '').startsWith('/staff')) this.$router.push('/')
          }
        }
      },
      /**
       * The other half of the router's /staff path gate. That gate exempts
       * navigations made while staff access is still RESOLVING (or a real staff
       * member would be bounced on every hard refresh), so a non-staff visitor
       * can slip onto a /staff route during that window; and the isStaff watcher
       * above only evicts on a true -> false TRANSITION, which someone who was
       * never staff never makes. Evict here the moment the read resolves.
       */
      staffAccessPending: function (pending) {
        if (pending || this.loggingOut) return
        if (!this.isStaff && (this.$route.path || '').startsWith('/staff')) {
          this.$router.push('/').catch(() => {})
        }
      },
      /**
       * Default-on for opted-in enterprises' staff. Not `immediate`: this cannot be due when
       * the instance is created (with a user the staff read is still pending, without one
       * nobody is signed in). The flag makes it exactly-once, since the navigation is
       * asynchronous (service-worker teardown first) and the inputs can flicker while it is in
       * flight.
       */
      vue3AutoOptInDue: function (due) {
        if (!due || this.vue3AutoOptInFired) return
        this.vue3AutoOptInFired = true
        // `$route` is read inside onReady, not here: every route is a lazy chunk, so `$route`
        // is still START (fullPath '/') until the first navigation resolves, and the staff
        // read can win that race - a deep link would land on the new app's root. onReady
        // calls back at once when the router is already ready. A first navigation that fails
        // comes in two orders. AFTER this callback is queued: vue-router drops it, the flag
        // stays latched, and there is no push on this page load. BEFORE: vue-router has
        // already marked itself ready, so the callback runs at once with `$route` still
        // START; the `matched` check below catches that.
        this.$router.onReady(() => {
          // Decided before the router was ready, acted on after: the browser may be signing
          // out by now. Un-latch as well, so a later sign-in on this page is still switched.
          if (!this.vue3AutoOptInDue) {
            this.vue3AutoOptInFired = false
            return
          }
          // START is the only route with no matched records (a real one, the 404 included,
          // has at least one), and its fullPath is not where this person is.
          if (this.$route.matched.length === 0) {
            this.vue3AutoOptInFired = false
            return
          }
          // No retry on failure: the cookie is written first, so shouldAutoOptIn is false from
          // here on, and the next page load is proxied to the new app anyway.
          switchToNewApp(this.$route.fullPath).catch(err => {
            console.log('Error switching to the new app:', err)
          })
        })
      },
      staffEnterpriseIDs: {
        handler: function (val) {
          this.staffEnterprisesLoaded = !!this.userToken
          if (this.staffEnterprisesLoaded) {

            // Choose a new enterprise override if on mobile
            if (Capacitor.isNative && !this.enterpriseOverride && val.length) {
              if (val.length === 1) this.enterpriseOverride = val[0]
              else this.forceEnterpriseOverrideSelection = true
            }
          }

          Object.keys(this.staffEnterpriseListeners).forEach(k => {
            if (!val.includes(k)) {
              this.staffEnterpriseListeners[k]()
              delete this.staffEnterpriseListeners[k]
            }
          })

          Object.keys(this.staffEnterpriseNotificationListeners).forEach(k => {
            if (!val.includes(k)) {
              this.staffEnterpriseNotificationListeners[k]()
              delete this.staffEnterpriseNotificationListeners[k]
            }
          })

          val.forEach(id => {
            if (!this.staffEnterpriseListeners[id]) {
              this.$set(this.staffEnterpriseListeners, id, onSnapshot(
                doc(this.db, 'enterprises', id),
                snap => {
                  if (!snap.exists()) {
                    this.$delete(this.staffEnterpriseNames, id)
                    this.$delete(this.staffEnterpriseURLs, id)
                    return
                  }
                  this.$set(this.staffEnterpriseNames, id, snap.data().name && snap.data().name !== 'formfactories' ? snap.data().name : snap.data().description)
                  const primaryDomain = ((snap.data().domains || []).find(d => d && d.domain) || {}).domain || ''
                  this.$set(this.staffEnterpriseURLs, id, primaryDomain)
                },
                err => {
                  console.log('Error listening to staff enterprises', err)
                },
              ))
            }

            if (id !== this.enterpriseID && !this.staffEnterpriseNotificationListeners[id]) {
              this.$set(this.staffEnterpriseNotificationListeners, id, onSnapshot(
                query(
                  collection(this.db, 'enterprises', id, 'users', this.$root.userID, 'notifications'),
                  where('read', '==', false),
                  limit(10)
                ),
                snap => {
                  this.$set(this.staffEnterpriseUnreadNotificationCount, id, snap.docs.length)
                },
                err => {
                  console.log('Error listening to staff enterprise notifications', err)
                },
              ))
            }
          })
        },
        immediate: true,
      },
      // Start/stop the (dev-only) all-enterprises listener with dev status, so non-devs never open it.
      isDev: {
        handler: function (val) {
          if (val) this.startDevTicketFeed()
          else this.stopDevTicketFeed()
        },
        immediate: true,
      },
      enterpriseID: {
        handler: function (val, oldVal) {
          window.enterpriseID = val
          if (oldVal && val && oldVal !== val) {
            // Switch enterprises
            console.log('switching enterprise to', val, 'from', oldVal)
          }
        },
        immediate: true,
      },
      enterprise: {
        handler: function (val) {
          window.enterprise = val
        },
        immediate: true,
      },
      cartOpen: function (val) {
        if (val && this.showProjectPreview) this.showProjectPreview = false
      },
      meilisearchKey: {
        handler: function(val) {
          if (!val) {
            this.$store.dispatch('search/clear')
            return
          }
          this.$store.dispatch('search/initialize', {
            enterpriseID: this.enterpriseID,
            searchKey: val,
          });
        },
      },
      lightTheme: {
        handler: function (val) {
          this.$vuetify.theme.themes.light.primary = val.color
        },
        immediate: true,
      },
      darkTheme: {
        handler: function (val) {
          this.$vuetify.theme.themes.dark.primary = val.color
        },
        immediate: true,
      },
      darkMode: {
        handler: function (val) {
          this.$vuetify.theme.dark = val
        },
        immediate: true,
      },
      darkModePreference: {
        handler: function (val) {
          window.localStorage.setItem('darkModePreference', val)
          if (this.userProfile && this.userProfile.prefs_useDarkMode !== val) {
            this.userProfile.prefs_useDarkMode = val
          }
        },
      },
      brandProperties: {
        handler: function (val) {
          if (!val) return
          Object.keys(val).forEach(key => {
            document.documentElement.style.setProperty(key, val[key])
          })
        },
        immediate: true,
      },
      showProjectPreview: function (val) {
        this.validateProjectPreview().catch(console.error)
        if (this.cartOpen) this.cartOpen = false
      },
      projectPreview: {
        immediate: true,
        handler: function (val) {
          if (!val) {
            const cur = localStorage.getItem('recentProject')
            if (cur) this.localRecentProject = cur
            else {
              this.$root.callFunction('createProject', { enterprise: this.$root.enterpriseID })
                .then(response => {
                  const uuid = response.data
                  localStorage.setItem('recentProject', uuid)
                  this.localRecentProject = uuid
                })
            }
          } else this.validateProjectPreview().catch(console.error)
        },
      },
      anonymousUserName: function (val) {
        this.saveAnonymousUserInfo()
      },
      anonymousUserEmail: function (val) {
        this.saveAnonymousUserInfo()
      },
      autoEmailConfirmationQueue: function (val) {
        if (val.length && !this.autoEmailConfirmation) this.autoEmailConfirmation = this.autoEmailConfirmationQueue.shift()
        this.showAutoEmailConfirmation = !!this.autoEmailConfirmation
      },
      autoEmailConfirmation: function (val) {
        if (!val && this.autoEmailConfirmationQueue) {
          const comp = this
          setTimeout(function () {
            if (!comp.autoEmailConfirmation) comp.autoEmailConfirmation = comp.autoEmailConfirmationQueue.shift()
          }, 800)
        }
        this.showAutoEmailConfirmation = !!this.autoEmailConfirmation
      },
      unreadNotifications: {
        immediate: true,
        handler: function (val) {
          if (Capacitor.isNativePlatform()) Badge.set({ count: val })
        },
      },
      showNotifications: function (newVal, oldVal) { app.updateNotifications() },
      notificationPage: function (newVal, oldVal) { app.listenToNotifications() },
      userProfile: {
        handler (val) {
          if (app.currentUser) {
            if (!app.state.skipChange.userProfile) {
              app.state.skipChange.userProfile = true
              val.dateModified = Date.now()
              // dashboardPresets is now owned by the append-only history subcollection
              // (dashboardPresetHistory). NEVER carry it in this whole-profile, no-merge
              // write — a stale/unrelated profile update here used to clobber the user's
              // saved views (last-write-wins). Strip it from a shallow copy so this write
              // can't touch it. The history listener mirrors the latest version back into
              // userProfile.dashboardPresets out of band (skipChange) for legacy readers.
              const profileToWrite = { ...val }
              delete profileToWrite.dashboardPresets
              updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'users', this.currentUser.uid), profileToWrite).catch(err => console.log('Error updating user profile', err))
              this.state.uploadingProfilePic = false
            } else app.state.skipChange.userProfile = false

            if (val.prefs_brand && this.isStaff) setTheme(val.prefs_brand)
          } else setTheme()
        },
        deep: true,
      },
    },
    created: function () {
      // Prevent leaving when jobs are uploading
      const app = this
      window.addEventListener('beforeunload', function (e) {
        if (app.activeUploads > 0) {
          e.preventDefault()
          e.returnValue = ''
        }
      })

      // Check if we have an invite code
      this.staffInviteID = new URLSearchParams(window.location.search).get('invite')

      // Capture paste events - useful for putting files into chats
      document.addEventListener('paste', e => app.$emit('paste', e))

      this.$on('keydown', this.keyDown)
      this.$on('keyup', this.keyUp)

      // Get enterprise updates
      onSnapshot(
        doc(this.db, 'enterprises', this.enterpriseID),
        snap => { if (snap.data()) this.enterpriseRaw = snap.data() },
        err => console.log('Error listening to enterprise snapshot', err),
      )
      onSnapshot(
        doc(this.db, 'configuration', 'public'),
        snap => { if (snap.data())this.publicConfiguration = snap.data() },
        err => console.log('Error listening to public configuration', err),
      )
      onSnapshot(
        doc(this.db, 'config', 'plans'),
        snap => { if (snap.data()) this.allTiers = snap.data().tiers },
        err => console.log('Error listening to plans', err),
      )
      onSnapshot(
        collection(this.db, 'developers'),
        snap => { this.developerList = snap.docs.map(d => Object.assign(d.data(), { uid: d.id })); this.developerListLoaded = true },
        err => console.log('Error listening to developers', err),
      )

      // Get default enterprise
      getDomainEnterprise().then(res => {
        this.domainEnterprise = { text: `Default (${res.enterprise.name})`, value: null, enterpriseID: res.id }
      })

      // Detect theme preference
      const storedPreference = window.localStorage.getItem('darkModePreference')
      if (storedPreference !== null) {
        this.darkModePreference = storedPreference === 'true'
      } else {
        // Default to dark mode if no preference is stored
        this.darkModePreference = true
      }

      // Prime notification sounds (browser autoplay needs a first user gesture)
      notificationSound.registerUnlock()

      // Refresh current time every second
      this.currentTimeInterval = setInterval(function () { app.currentTime = Date.now() }, 1000)
      this.currentTimeMinutesInterval = setInterval(function () { app.currentTimeMinutes = Date.now() }, 60000)

      // Handle mobile push notifications
      if (Capacitor.isNativePlatform()) {
        // On registration of notification permission, register the token with the user
        PushNotifications.addListener(
          'registration',
          function (token) {
            if (!app.currentUser || !token.value) return
            app.notificationToken = token.value
            setDoc(
              doc(app.db, 'users', app.currentUser.uid, 'devices', app.notificationToken),
              { active: true, dateModified: Date.now() },
              { merge: true },
            ).catch(err => console.log('Error registering notification token', err))
          }
        )

        // Method called when tapping on a notification
        PushNotifications.addListener(
          'pushNotificationActionPerformed',
          function (action) {
            const notification = JSON.parse(action.notification.data.json)
            app.openMobileNotification(notification)
          },
        )
      }
    },
    mounted: function () {
      this.listenToTeams()

      // Restore any saved user info
      if (localStorage.getItem('anonymousUserName') !== null) this.anonymousUserName = localStorage.getItem('anonymousUserName')
      if (localStorage.getItem('anonymousUserEmail') !== null) this.anonymousUserEmail = localStorage.getItem('anonymousUserEmail')

      // Listen to catalog and sales options
      const app = this
      onSnapshot(
        doc(this.db, 'enterprises', this.enterpriseID, 'config', 'production'),
        docSnap => {
          if (docSnap.exists()) this.production = docSnap.data()
        },
        err => console.log('Error in catalog snapshot listening:', err),
      )

      // Listen to general config settings
      onSnapshot(
        doc(this.db, 'enterprises', this.enterpriseID, 'config', 'public'),
        docSnap => {
          if (docSnap.exists()) this.configPublic = docSnap.data()
          this.newAppForStaffAtLoad = latchNewAppForStaff(this.newAppForStaffAtLoad, docSnap.exists() ? docSnap.data() : null)
          this.configPublicLoaded = true
        },
        err => {
          console.log('Error in public config snapshot listening:', err)
          this.newAppForStaffAtLoad = latchNewAppForStaff(this.newAppForStaffAtLoad, null)
          this.configPublicLoaded = true
        },
      )

      // Listen to selling points collection
      onSnapshot(
        query(
          collection(this.db, 'enterprises', this.enterpriseID, 'sellingPoints'),
          where('active', '==', true),
        ),
        snap => {
          const points = {}
          snap.docs.forEach(d => { points[d.id] = d.data() })
          this.sellingPoints = points
        },
        err => console.log('Error in selling points snapshot listening:', err),
      )

      // Listen to sales options collection
      onSnapshot(
        doc(this.db, 'enterprises', this.enterpriseID, 'reference', 'salesOptions'),
        docSnap => {
          if (docSnap.exists()) this.salesOptions = docSnap.data()
        },
        err => console.log('Error in salesOptions snapshot listening:', err),
      )

      // Get all published tasks
      onSnapshot(
        query(
          collection(this.$root.db, 'enterprises', this.$root.enterpriseID, 'chats'),
          where('published', '==', true),
          where('active', '==', true),
        ),
        snap => {
          this.publishedTasks = snap.docs.map(d => Object.assign(d.data(), { uuid: d.id }))
        },
        err => console.log('Error listening to published tasks snapshot', err),
      )

      // Get all sales tax rates
      onSnapshot(
        collection(this.$root.db, 'enterprises', this.$root.enterpriseID, 'customTaxes'),
        snap => {
          this.taxRates = snap.docs.map(d => Object.assign(d.data(), { id: d.id })).sort((a, b) => a.order - b.order)
        },
        err => console.log('Error listening to tax rate snapshot', err),
      )

      // Check if any notification needs to be opened
      if (localStorage.getItem('notificationToOpen')) {
        this.openMobileNotification(JSON.parse(localStorage.getItem('notificationToOpen')))
        localStorage.removeItem('notificationToOpen')
      }
    },
    methods: {
      subscribeToMachine: function (machineID, scope = ['printerState'], callback) {
        // The gateway-ingest machine-state server has been removed, so there is no
        // live machine state to stream. Kept as a no-op so existing callers still
        // receive a valid unsubscribe function and don't break.
        return () => {}
      },
      startRecordingFailureEvent: function (machineID, jobID, projectID) {
        this.queuedFailureEvent = { machineID, jobID, projectID }
        this.openMachinePanel(machineID)
      },
      lightenedThemeColor: function (amount) {
        if (amount < 0) return this.darkenedThemeColor(-amount)
        return this.blendColors(this.theme.color, '#FFFFFF', amount)
      },
      darkenedThemeColor: function (amount) {
        if (amount < 0) return this.lightenedThemeColor(-amount)
        return this.blendColors(this.theme.color, '#000000', amount)
      },
      previewProject: function (projectID) {
        if (projectID) {
          this.projectPreviewID = projectID
          this.projectPreviewOpen = true
        } else {
          this.projectPreviewOpen = false
        }
      },
      previewJob: function (jobID) {
        if (jobID) {
          this.jobPreviewID = jobID
          this.jobPreviewOpen = true
        } else {
          this.jobPreviewOpen = false
        }
      },
      getStripeTaxCodes: function () {
        if (this.cachedStripeTaxCodes && this.cachedStripeTaxCodesRetrieved > Date.now() - 1000 * 60 * 60 * 24) return Promise.resolve(this.cachedStripeTaxCodes)
        else if (this.cachedStripeTaxCodesPromise) return this.cachedStripeTaxCodesPromise
        else {
          this.cachedStripeTaxCodesPromise = this.callFunction('listTaxCodes', { enterprise: this.$root.enterpriseID })
            .then(response => {
              this.cachedStripeTaxCodes = response.data
              this.cachedStripeTaxCodesRetrieved = Date.now()
              this.cachedStripeTaxCodesPromise = null
              return this.cachedStripeTaxCodes
            })
          return this.cachedStripeTaxCodesPromise
        }
      },
      optionsToShow: function (model, hideBaseOptions = false) {
        const results = []

        // Match all options that are currently selected
        const optionPicks = []
        const optionPicksData = []
        this.$root.catalog.forEach((option, o) => {
          const match = this.matchOptionFromModel(option.name, model)
          optionPicks.push(match)
          optionPicksData.push(match ? this.$root.catalogByName[option.name].children[match] : null)
        })

        // Add all options that are compatible with the current selections
        this.$root.catalog.forEach((option, o) => {
          const compatibleChildren = []
          option.children.forEach(child => {
            let compatible = true

            // Check compatibility with previous options
            for (let i = 0; i < o; i++) {
              const previousOption = this.$root.catalog[i]
              const previousOptionPick = optionPicks[i]
              const previousOptionPickData = optionPicksData[i]

              if (!previousOptionPick || !previousOptionPickData) continue
              if (!this.$root.catalogCompatibilityMap[option.name][child.name][previousOption.name][previousOptionPickData.name]) {
                compatible = false
                break
              }
            }

            if (compatible) compatibleChildren.push(child.name)
          })

          if (compatibleChildren.length) {
            results.push({ ...option, children: compatibleChildren })
          }
        })

        return hideBaseOptions ? results.filter(x => !['Technology', 'Material', 'Color'].includes(x.name)) : results
      },
      keyUp: function (e) {
        this.$emit('key-up-code', e.code)
        if (e.code === 'Space') this.spaceUnpressed(e)
      },
      keyDown: function (e) {
        this.$emit('key-down-code', e.code)
        if (e.code === 'Space') this.spacePressed(e)
      },
      spaceUnpressed: function (e) {
        // Ignore if a field is being edited
        if (document.activeElement && document.activeElement.id !== 'search-query' && (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) || document.activeElement.contentEditable === 'true')) return

        // Close project preview if space was pressed over a second ago
        this.spaceDown = false
        if (this.projectPreviewOpen && this.spaceHoldDeadline && this.spaceHoldDeadline < Date.now()) {
          e.preventDefault()
          this.spaceHoldDeadline = null
          this.projectPreviewOpen = false
        }
      },
      spacePressed: function (e) {
        // Ignore if a field is being edited
        if (document.activeElement && document.activeElement.id !== 'search-query' && (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) || document.activeElement.contentEditable === 'true')) return

        // Prevent scrolling down page if holding space to preview project
        if (this.spaceDown) {
          if (this.projectPreviewOpen) e.preventDefault()
          return
        } else this.spaceDown = true

        if (this.projectPreviewOpen) {
          e.preventDefault()
          this.spaceHoldDeadline = null
          this.projectPreviewOpen = false
          return
        }

        this.spaceHoldDeadline = Date.now() + 1000
        this.$emit('space-pressed', e)
      },
      openMachinePanel(machineID) {
        this.machinePanelID = machineID
        this.machinePanelOpen = true
        // Reset to default panel tab (0) when opened from navigation
        this.machinePanelDefaultTab = 0
        // Clear any previous time range
        this.machinePanelInitialTimeRange = null
        // Set unique key to force component recreation
        this.machinePanelKey = Date.now()

      },
      // Returns a hash of the models color, including color changes
      modelColorName: function (model, modelData) {
        return modelColorName(model, modelData)
      },
      // Both live in @/utils/modelThumbnail so they can be unit tested; main.js can't be imported.
      modelThumbnailDetails: function (modelDataOrProject = {}, model) {
        return modelThumbnailDetails(modelDataOrProject, model, this.modelExtensions)
      },
      // Generate and cache a PDF first-page thumbnail as a data URL in state.models[uuid].pdfThumb
      async generatePdfThumbnail (pdfUrl, uuid) {
        try {
          const pdfjsLib = await this.loadPdfJsLib()
          const loadingTask = pdfjsLib.getDocument({ url: pdfUrl, disableWorker: true })
          const pdf = await loadingTask.promise
          const page = await pdf.getPage(1)
          const viewport = page.getViewport({ scale: 1.0 })
          // Target a consistent width for thumbnails
          const targetWidth = 256
          const scale = Math.max(0.1, targetWidth / viewport.width)
          const scaledViewport = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          canvas.width = Math.ceil(scaledViewport.width)
          canvas.height = Math.ceil(scaledViewport.height)
          const renderContext = { canvasContext: context, viewport: scaledViewport }
          await page.render(renderContext).promise
          const dataUrl = canvas.toDataURL('image/png')
          if (!this.state) this.state = { models: {} }
          if (!this.state.models[uuid]) this.$set(this.state.models, uuid, {})
          this.$set(this.state.models[uuid], 'pdfThumb', dataUrl)
          return dataUrl
        } catch (e) {
          // Swallow errors; a static icon will be used instead
          return null
        }
      },
      // Lazy-load pdf.js from CDN to avoid bundler dependency
      async loadPdfJsLib () {
        try {
          if (typeof window !== 'undefined' && window.pdfjsLib) return window.pdfjsLib
          await new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js'
            script.async = true
            script.onload = () => resolve()
            script.onerror = (e) => reject(e)
            document.head.appendChild(script)
          })
          return window.pdfjsLib || null
        } catch (e) {
          return null
        }
      },
      designThumbnailDetails: function (design) {
        if (!design) return { loading: false, url: '' }
        const configurationColors = (design.configurations || []).map(c => c.settings.Color).filter(c => c)
        const validConfigurationColors = configurationColors.filter(c => design.colorThumbnails && design.colorThumbnails[c])
        let url = validConfigurationColors.length ? design.colorThumbnails[configurationColors[0]] : design.thumbnail
        let loading = configurationColors.length && (!design.colorThumbnails || !design.colorThumbnails[configurationColors[0]])
        return { loading, url }
      },
      modelThumbnail: function (modelDataOrProject, model) {
        return this.modelThumbnailDetails(modelDataOrProject, model).url
      },
      roughSizeOfObject: function ( object ) {
        var objectList = [];
        var stack = [ object ];
        var bytes = 0;
        while ( stack.length ) {
            var value = stack.pop();

            if ( typeof value === 'boolean' ) {
                bytes += 4;
            }
            else if ( typeof value === 'string' ) {
                bytes += value.length * 2;
            }
            else if ( typeof value === 'number' ) {
                bytes += 8;
            }
            else if
            (
                typeof value === 'object'
                && objectList.indexOf( value ) === -1
            )
            {
                objectList.push( value );

                for( var i in value ) {
                    if (['cssRules'].includes(i)) return
                    stack.push( value[ i ] );
                }
            }
        }
        return bytes;
      },
      resetToken: function () {
        const wasStaff = this.isStaff
        console.log('[auth resetToken] start', {
          enterpriseID: this.enterpriseID,
          userID: this.currentUser?.uid || null,
          wasStaff,
          currentEnterpriseClaims: this.userToken?.claims?.enterprises?.[this.enterpriseID] || null,
        })
        this.currentUser.getIdToken(true)
          .then(() => this.currentUser ? auth.currentUser.getIdTokenResult() : Promise.resolve(null))
          .then(token => {
            this.userToken = token
            window.userToken = token
            const isStaffNow = this.isStaff
            console.log('[auth resetToken] refreshed', {
              enterpriseID: this.enterpriseID,
              userID: this.currentUser?.uid || null,
              isStaffNow,
              currentEnterpriseClaims: token?.claims?.enterprises?.[this.enterpriseID] || null,
              enterpriseClaimKeys: Object.keys(token?.claims?.enterprises || {}),
            })
            return updateDoc(doc(db, 'enterprises', this.enterpriseID, 'users', this.currentUser.uid), { tokenExpired: false })
              .catch(err => console.log('Error resetting token', err))
              .then(() => {
                if (!this.currentEnterpriseStaffAccessLoaded || (this.currentEnterpriseStaffAccess || 0) < 5) {
                  this.listenToCurrentEnterpriseStaffAccess(this.currentUser?.uid)
                }
                if (!wasStaff && isStaffNow) window.location.reload()
              })
          })
      },
      uploadJob: function (id, file) {
        // Start uploading
        const storageRef = ref(this.storage, `jobs/${id}/${file.name}`)
        const uploadTask = uploadBytesResumable(storageRef, file)

        // Upload and keep track of status
        const app = this
        this.activeUploads++
        uploadTask.on('state_changed', function (snapshot) {
          updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'jobs', id), {
            uploadProgress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            dateModified: Date.now(),
            lastUpdatedBy: app.userID,
          })
            .catch(error => console.log('Error updating job upload progress', error))
        }, function (error) {
          console.log('Error uploading job', file.name, error)
          updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'jobs', id), {
            uploadProgress: deleteField(),
            dateModified: Date.now(),
            lastUpdatedBy: app.userID,
          })
            .catch(error => console.log('Error updating job upload progress', error))
          app.activeUploads--
        }, function () {
          // Get link to file
          updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'jobs', id), {
            uploadProgress: 100,
            dateModified: Date.now(),
            lastUpdatedBy: app.userID,
          })
            .catch(error => console.log('Error updating job upload progress', error))
          getDownloadURL(storageRef)
            .then(url => {
              return updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'jobs', id), {
                url,
                dateModified: Date.now(),
                lastUpdatedBy: app.userID,
              })
            })
            .then(() => {
              app.activeUploads--
            })
            .catch(error => {
              console.log('Error getting link to job url for', file.name, error)
              app.activeUploads--
            })
        })
      },
      showError: function (error, type = 'error') {
        const id = this.generateUUID()
        this.errors.push({
          message: error,
          type,
          time: Date.now(),
          id,
        })
        // Auto-remove from the array (the snackbars use :timeout="-1" and are driven purely by
        // this array) so it reflects only currently-visible toasts — keeps the "+N more" cap
        // accurate instead of counting toasts Vuetify had merely hidden but left in the array.
        setTimeout(() => { this.errors = this.errors.filter(e => e.id !== id) }, 6000)
      },
      notifyUserOnFailure: async function (promise, popupMessage, timeoutInMs) {
        let timeout = timeoutInMs > 0 ? timeoutInMs : 60000;
        await Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error(`Promise failed to resolve after ${timeout}ms`)), timeout))
        ])
          .catch((err) => {
            this.showError(popupMessage || 'An error has occurred');
            console.error(err);
          });
      },
      startWorkerTask: function (type, data) {
        return new Promise(resolve => {
          const id = this.generateUUID()
          this.workerTasks[id] = resolve
          this.worker.postMessage({ id, type, data })
        })
      },
      // Setup worker for some async tasks
      setupWorker: function () {
        if (window.Worker) {
          this.worker = new Worker('/worker.js')
          this.worker.onmessage = (e) => {
            if (this.workerTasks[e.data.id]) {
              this.workerTasks[e.data.id](e.data.data)
              delete this.workerTasks[e.data.id]
            }
          }
        }
      },
      productPrice: function (product, quantity = 1, variantID) {
        // Get pricing model
        let item = product
        const variant = product.variantData[variantID || product.variants[0]]
        if (variant.customPricing) item = variant

        // Get best applicable price
        const availablePrices = item.prices.filter(x => x.price && x.minQuantity <= quantity).map(x => x.price)
        return Math.min(...availablePrices)
      },
      blendColors: function (colorA, colorB, amount) {
        if (amount > 1) amount = 1
        if (typeof colorA === 'string') {
          const [rA, gA, bA] = colorA.match(/\w\w/g).map((c) => parseInt(c, 16))
          const [rB, gB, bB] = colorB.match(/\w\w/g).map((c) => parseInt(c, 16))
          const r = Math.round(rA + (rB - rA) * amount).toString(16).padStart(2, '0')
          const g = Math.round(gA + (gB - gA) * amount).toString(16).padStart(2, '0')
          const b = Math.round(bA + (bB - bA) * amount).toString(16).padStart(2, '0')
          return `#${r}${g}${b}`
        } else {
          const [rA, gA, bA] = colorA
          const [rB, gB, bB] = colorB
          const r = Math.round(rA + (rB - rA) * amount)
          const g = Math.round(gA + (gB - gA) * amount)
          const b = Math.round(bA + (bB - bA) * amount)
          return [r, g, b]
        }
      },
      callFunction: function (method, data) {
        return httpsCallable(this.functions, method)(data)
      },
      deleteAccount: function () {
        if (auth.currentUser) {
          return deleteUser(auth.currentUser)
            .catch(error => {
              console.log('Error deleting account:', error)
              throw new Error('Error deleting account, please contact us for assistance.')
            })
        } else return Promise.reject(new Error('No user is logged in'))
      },
      saveRecentProject: function (uuid) {
        // Save to recent projects
        if (this.userProfile) {
          const maxProjects = 20
          if (!this.userProfile.recentProjects) this.$set(this.userProfile, 'recentProjects', [])
          if (this.userProfile.recentProjects.includes(uuid)) this.userProfile.recentProjects = this.userProfile.recentProjects.filter(x => x !== uuid)
          this.userProfile.recentProjects.push(uuid)
          if (this.userProfile.recentProjects.length > maxProjects) this.userProfile.recentProjects = this.userProfile.recentProjects.slice(this.userProfile.recentProjects.length - maxProjects)
        }
      },
      // Resolves to the id of a cart the customer can still add to. A cart that is gone, status 0/3,
      // or already a placed order (checked out, invoiced or paid; the server locks its contents, see
      // utils/placedOrder) is replaced by a fresh project. Staff keep the selected project as is.
      validateProjectPreview: function () {
        const current = this.projectPreview
        if (!current || this.isStaff) return Promise.resolve(current)
        if (this.cartReplacementPromise) return this.cartReplacementPromise
        return getDoc(doc(this.$root.db, 'enterprises', this.enterpriseID, 'projects', current)).then(docSnap => {
          if (isUsableCartProject(docSnap.data())) return current
          if (!this.cartReplacementPromise) {
            this.cartReplacementPromise = this.$root.callFunction('createProject', { enterprise: this.$root.enterpriseID })
              .then(response => {
                const uuid = response.data
                localStorage.setItem('recentProject', uuid)
                this.localRecentProject = uuid
                this.saveRecentProject(uuid)
                return uuid
              })
              .finally(() => {
                this.cartReplacementPromise = null
              })
          }
          return this.cartReplacementPromise
        })
      },
      cssValidNumber: function (val) {
        if (val === null || val === '' || val === undefined) return ''
        if (isNaN(+val)) return val
        else return +val + 'px'
      },
      materialToTask: function (material) {
        if (!material) return null;

        const CREATOR = 'P1wkQjM9JHPNQcBk3VkVNAzBNSn1';
        const now = Date.now();

        // Cache frequently accessed data
        const catalog = this.$root.catalog || [];
        const technologyCatalog = _.find(catalog, { name: 'Technology' }) || {};
        const materialCatalog = _.find(catalog, { name: 'Material' }) || {};
        const colorCatalog = _.find(catalog, { name: 'Color' }) || {};

        // Get technologies once using lodash
        const matTechnologies = _.filter(
          technologyCatalog.children || [],
          tech => _.includes(material.restrictions.Technology, tech.name)
        );

        // Pre-compute technology display text
        const technologyText = matTechnologies
          .map(tech => {
            if (!tech.task || !this.$root.publicTaskIDToPathMap[tech.task]) {
              return tech.displayName;
            }
            return `<a href="/${this.$root.publicTaskIDToPathMap[tech.task]}">${tech.displayName}</a>`;
          })
          .join(', ');

        // Helper to create base element
        const createBaseElement = () => ({
          count: 0,
          userTags: [],
          created: now,
          createdBy: CREATOR
        });

        // Helper to create element with base properties using lodash merge
        const createElement = (props) => _.assign({}, createBaseElement(), props);

        // Get available colors once using lodash chain
        const availableColors = _.chain(material.restrictions?.Color || [])
          .map(c => _.find(colorCatalog.children, { name: c }))
          .filter(color => color?.active && (this.$root.isStaff || !color.advanced))
          .value();

        // Check for image file extensions more efficiently
        const isImageFile = (url) => /\.(png|jpe?g)$/i.test(url);

        // Build task element data more efficiently
        const taskElementData = {
          maximumSizeLabel: createElement({
            type: 'h3',
            text: '<font color="#2196f3">maximum size</font>',
          }),
          availableColorsLabel: createElement({
            text: 'available colors',
            type: 'h3',
          }),
          longDescription: createElement({
            type: 'text',
            marginTop: 2,
            text: material.longDescription,
          }),
          resolutionLabel: createElement({
            text: '<font color="#2196f3">resolution</font>',
            type: 'h3',
          }),
          headerGroup: createElement({
            type: 'group',
            horizontal: true,
            taskElements: ['headerImage', 'headerDetailsGroup'],
            text: '',
          }),
          descriptionAddon: createElement({
            type: 'text',
            text: material.descriptionAddon,
          }),
          materialSellingPoints: createElement({
            text: '',
            type: 'component',
            component: 'material-selling-points',
            material: material.uuid,
          }),
          technology: createElement({
            text: `<b><font color="#555">Technology: ${technologyText}</font></b>`,
            type: 'text',
            marginTop: -2,
          }),
          allStatsGroup: createElement({
            type: 'group',
            horizontal: true,
            taskElements: ['propertiesGroup', 'statsGroup'],
          }),
          detailsGroup: createElement({
            taskElements: ['colors', 'allStatsGroup'],
            text: '',
            type: 'group',
          }),
          compareTable: createElement({
            type: 'component',
            component: 'material-chart',
            material: material.uuid,
            text: '',
          }),
          advantageGroup: createElement({
            type: 'group',
            card: true,
            taskElements: [],
            strip: false,
            horizontal: true,
            text: '',
          }),
          headerImage: createElement({
            text: '',
            type: 'text',
            attachments: [{
              height: 250,
              name: 'Image-empty-state.jpg',
              url: _.get(material, 'images[0].url', material.image),
              uuid: 'header-image-attachment',
              uploaded: true,
            }],
          }),
          videoHighlight: createElement({
            text: '',
            type: 'text',
            attachments: material.video ? [{
              height: 250,
              name: `${material.displayName} Showcase${isImageFile(material.video) ? '.jpg' : ''}`,
              url: material.video,
              external: !isImageFile(material.video),
              uuid: 'youtube-video',
              uploaded: true,
            }] : [],
          }),
          datasheet: createElement({
            text: material.datasheet
              ? `<a target="_blank" href="${material.datasheet}"><i class="mr-2 fas fa-file-alt"></i>View Data Sheet</a>`
              : '',
            type: 'h3',
          }),
          resolutionGroup: createElement({
            text: '',
            type: 'group',
            taskElements: ['resolutionLabel', 'resolutionText'],
          }),
          colors: createElement({
            text: '<font color="#2196f3">available colors</font>',
            colors: availableColors,
            type: 'h3',
            marginBottom: 4,
            marginTop: 0,
            marginLeft: 4,
            marginRight: 4,
          }),
          materialGuideLink: createElement({
            type: 'h1',
            marginBottom: 16,
            marginTop: 0,
            text: '<a href="/material-guide"><div style="text-align: right;"><font color="#2196f3">material guide &gt;</font></div></a>',
          }),
          resolutionText: createElement({
            text: material.resolution,
            type: 'text',
          }),
          statsGroup: createElement({
            text: '',
            type: 'group',
            marginTop: 8,
            marginLeft: 12,
            marginRight: 4,
            taskElements: ['buildVolumeGroup', 'resolutionGroup', 'datasheet'],
          }),
          headerDetailsGroup: createElement({
            type: 'group',
            text: '',
            justifyVertical: 'center',
            taskElements: ['headerTitle', 'technology', 'longDescription', 'materialSellingPoints'],
          }),
          helpLabel: createElement({
            type: 'h1',
            text: '<font color="#2196f3">need help choosing a material?</font>',
          }),
          compareLabel: createElement({
            type: 'h2',
            text: `<font color="#2196f3">compare with other ${
              matTechnologies
                .map(x => _.toLower((x.longName || '')
                  .split(' ')
                  .map(s => s === _.toUpper(s) ? s : _.toLower(s))
                  .join(' ')))
                .join(', ')
            }</font>`,
          }),
          headerTitle: createElement({
            text: `<font color="#2196f3">${material.displayName}</font>`,
            type: 'h1',
            marginTop: 0,
          }),
          buildVolumeText: createElement({
            text: `${material.maxBuildSize}&nbsp;<br>${material.maxBuildSizeInches}&nbsp;<br>`,
            type: 'text',
          }),
          propertiesGroup: createElement({
            taskElements: ['propertiesLabel'],
            text: '',
            type: 'group',
          }),
          propertiesLabel: createElement({
            text: '<font color="#2196f3">properties</font>',
            type: 'h3',
            marginTop: 4,
            marginBottom: 2,
          }),
          buildVolumeGroup: createElement({
            taskElements: ['maximumSizeLabel', 'buildVolumeText'],
            text: '',
            type: 'group',
          }),
          guideLabel: createElement({
            text: '<div style="text-align: right;"><font color="#2196f3">or browse by use case in our&nbsp;</font></div>',
            type: 'h2',
            marginTop: 12,
          }),
          descriptionGroup: createElement({
            taskElements: ['descriptions', 'detailsGroup'],
            horizontal: true,
            strip: false,
            backgroundColor: '#0001',
            type: 'group',
            text: '',
          }),
          advantagesLabel: createElement({
            type: 'p',
            text: '',
          }),
          descriptions: createElement({
            type: 'group',
            taskElements: ['descriptionAddon', 'videoHighlight'],
            text: '',
          }),
        };

        // Base task elements
        const taskElements = [
          'headerGroup',
          'descriptionGroup',
          'advantagesLabel',
          'advantageGroup',
          'helpLabel',
          'compareLabel',
          'compareTable',
          'guideLabel',
          'materialGuideLink',
        ];

        // Add gallery if there are multiple images
        if (_.size(material.images) > 1) {
          const galleryImages = _.slice(material.images, 1);

          if (galleryImages.length <= 3) {
            const showcaseElements = _.map(galleryImages, (img, i) => {
              const key = `imageShowcase${i}`;
              const textKey = `imageShowcaseText${i}`;

              taskElementData[key] = createElement({
                text: '',
                type: 'group',
                taskElements: [textKey],
                backgroundFile: img.url,
                backgroundContain: true,
              });

              taskElementData[textKey] = createElement({
                text: '',
                type: 'text',
                marginBottom: 80,
              });

              return key;
            });

            taskElementData.galleryShowcase = createElement({
              text: '',
              type: 'group',
              height: '500px',
              horizontal: true,
              taskElements: showcaseElements,
            });

            taskElements.splice(4, 0, 'galleryShowcase');
          } else {
            taskElementData.galleryGroup = createElement({
              taskElements: ['galleryCollage'],
              type: 'group',
              backgroundColor: '#0001',
              text: '',
            });

            taskElementData.galleryCollage = createElement({
              text: '',
              type: 'p',
              attachmentLayout: 'collage',
              attachments: _.map(galleryImages, (img, i) =>
                _.assign({ uuid: i, height: 400 }, img)
              ),
            });

            taskElements.splice(4, 0, 'galleryGroup');
          }
        }

        // Replace material names with links using lodash
        const materials = _.filter(
          materialCatalog.children || [],
          m => !m.advanced && m.active && m.uuid !== material.uuid
        );

        if (materials.length > 0) {
          // Create a map of material names to links using lodash keyBy
          const materialLinkMap = _.fromPairs(
            _.map(materials, mat => [
              mat.name,
              `<a href="/materials/${this.materialIDToPathMap[mat.uuid]}">${mat.name}</a>`
            ])
          );

          // Create temp element once for text extraction
          const tempDiv = document.createElement('div');

          // Use lodash to iterate over elements with text
          _.forEach(_.filter(taskElementData, 'text'), (e) => {
            tempDiv.innerHTML = e.text;
            const textContent = tempDiv.textContent;

            if (textContent && textContent !== material.name) {
              _.forEach(materials, mat => {
                const link = materialLinkMap[mat.name];
                e.text = _.replace(e.text, new RegExp(`\\b(${_.escapeRegExp(mat.name)})\\b`, 'gm'), link);
              });
            }
          });
        }

        // Add material property bars using lodash forEach
        _.forEach(this.$root.materialProperties, (p, i) => {
          const barKey = `${p.text}Bar`;
          taskElementData.propertiesGroup.taskElements.push(barKey);
          taskElementData[barKey] = createElement({
            text: p.text,
            type: 'component',
            component: 'stat-bar',
            value: material[p.value],
            color: p.color,
            marginTop: i ? 3 : 0,
            min: p.min,
            max: p.max,
            icon: p.icon,
          });
        });

        // Fill in advantages
        const advantages = material.advantages || [];
        if (_.isEmpty(advantages)) {
          // Remove advantage elements if none exist using lodash without
          _.pull(taskElements, 'advantageGroup', 'advantagesLabel');
        } else {
          _.forEach(advantages, (advantage, a) => {
            const hasImage = !!advantage.image;
            const isYouTube = hasImage && _.includes(advantage.image, 'youtube');

            taskElementData[`advantageTitle${a}`] = createElement({
              text: advantage.title || '',
              marginTop: 0,
              attachments: hasImage ? [{
                uuid: '264b3774-c1d9-4be8-bb4b-d3a2cb4dda39',
                uploaded: true,
                height: 150,
                external: isYouTube,
                name: `${advantage.title || ''}${isYouTube ? '' : '.jpg'}`,
                url: advantage.image,
              }] : [],
              type: 'h3',
            });

            taskElementData[`advantageText${a}`] = createElement({
              text: advantage.description,
              type: 'text',
            });

            taskElementData[`advantageGroup${a}`] = createElement({
              text: '',
              taskElements: [`advantageTitle${a}`, `advantageText${a}`],
              card: false,
              type: 'group',
            });

            taskElementData.advantageGroup.taskElements.push(`advantageGroup${a}`);
          });
        }

        // Return complete task object
        return {
          readAccess: ['public'],
          dateModified: now,
          taskElements,
          taskElementData,
          lastUpdatedBy: now,
          material: true,
          active: true,
          writeAccess: ['staff'],
          type: 'task',
          taggedUsers: [],
          list: _.get(matTechnologies, '[0].uuid', 'd4d42e4d-1fa0-4252-af8e-8c84b7bc4ce7'),
          created: now,
          members: [CREATOR],
          readerVisibility: ['public'],
          createdBy: CREATOR,
          uuid: material.uuid,
        };
      },
      hasPermission (permission) {
        return this.isAdmin || this.myPermissions.includes(permission)
      },
      hasAnyPermission (permissions) {
        return permissions.some(p => this.hasPermission(p))
      },
      canShowLocation (loc) {
        return !loc || !this.locationView || loc === this.locationView
      },
      canShowAnyLocation (locs) {
        return !locs || !this.locationView || locs.includes(this.locationView)
      },
      downloadPDF: function (project, type, partialModels) {
        // Sends the ID token the way callAPI does (callAPI can't take responseType/params). The
        // /documents route is anonymous today; the token is what lets the API gate it to staff and
        // the project's own customer.
        const idTokenPromise = this.currentUser ? this.currentUser.getIdToken(true) : Promise.resolve('')
        return idTokenPromise.then(idToken => {
          const request = {
            url: `${this.apiPath}/documents/${this.enterpriseID}/${project}/${type}`,
            method: 'GET',
            responseType: 'blob',
            headers: { Authorization: idToken },
          }
          if (partialModels) {
            request.params = { quantities: JSON.stringify(partialModels) }
          }
          return Axios(request)
        }).then(response => {
          // Get filename
          const basename = response.headers['content-disposition'].split('filename=')[1].split('.')[0]
          const extension = response.headers['content-disposition'].split('.')[1].split(';')[0]
          const filename = `${basename}.${extension}`

          // Download file
          downloadBlob(new Blob([response.data]), filename);
        })
      },
      downloadInventoryDataAsCSV: async function () {
        try {
          const headersBase = ['Category', 'Name', 'Brand', 'Stock', 'Min Stock', 'Max Stock', 'Location', 'URL', 'Date Modified', 'Last Updated By']
          const management = this.management || {}
          const customFields = (management.inventoryCustomFields || []).map(f => ({ uuid: f.uuid, name: f.name, values: Array.isArray(f.values) ? f.values : [] }))
          const customHeaders = customFields.map(f => f.name)
          const headers = [...headersBase, ...customHeaders]

          // load inventory collection client-side (respecting locationView if set)
          const invRef = this.locationView
            ? query(collection(this.db, 'enterprises', this.enterpriseID, 'inventory'), where('location', 'in', [this.locationView, '']))
            : collection(this.db, 'enterprises', this.enterpriseID, 'inventory')
          const snap = await getDocs(invRef)

          const locations = ((this.management || {}).locations || []).reduce((acc, loc) => { acc[loc.id] = loc.name; return acc }, {})
          const users = ((this.users || [])).reduce((acc, u) => { acc[u.id] = u.fullName; return acc }, {})
          const catMap = ((management.inventoryCategories || [])).reduce((acc, c) => { acc[c.uuid] = c.name; return acc }, {})

          const rows = []
          snap.forEach(docSnap => {
            const data = docSnap.data() || {}
            const row = []
            row.push(catMap[data.category] || '')
            row.push(data.name || '')
            row.push(data.brand || '')
            row.push(data.stock || '')
            row.push(data.minStock || '')
            row.push(data.maxStock || '')
            row.push(locations[data.location] || '')
            row.push(data.url || '')
            row.push(data.dateModified ? new Date(data.dateModified).toLocaleDateString('en-US', {year: 'numeric', month: '2-digit', day: '2-digit'}) : '')
            row.push(users[data.lastUpdatedBy] || '')
            // custom fields
            customFields.forEach(f => {
              const raw = (data.custom || {})[f.uuid]
              const arr = Array.isArray(raw) ? raw : (raw ? [raw] : [])
              const names = arr.map(v => {
                if (typeof v === 'string') {
                  const match = (f.values || []).find(x => x && x.uuid === v)
                  return match ? (match.name || '') : v
                } else if (v && typeof v === 'object') {
                  return v.name || ''
                } else return ''
              }).filter(Boolean)
              row.push(names.join('; '))
            })
            rows.push(row)
          })

          // Build CSV
          let csvContent = headers.join(',') + '\n'
          rows.forEach(r => {
            const safe = r.map(v => {
              const s = String(v || '')
              // Escape commas/quotes
              if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"'
              return s
            })
            csvContent += safe.join(',') + '\n'
          })

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
          const ts = new Date().toISOString().slice(0,10)
          downloadBlob(blob, `inventory_${ts}.csv`)
        } catch (e) {
          console.error('Inventory CSV export failed', e)
        }
      },
      isInTeams: function (teams) {
        const fixedTeams = Array.isArray(teams) ? teams : (teams ? [teams] : [])
        return this.myTeams.some(t => fixedTeams.includes(t.uuid))
      },
      saveAnonymousUserInfo: function () {
        localStorage.setItem('anonymousUserName', this.anonymousUserName)
        localStorage.setItem('anonymousUserEmail', this.anonymousUserEmail)
      },
      callAPI: function (method, path, data) {
        const comp = this
        const idTokenPromise = this.currentUser ? this.currentUser.getIdToken(true) : Promise.resolve('')
        return idTokenPromise.then(idToken => Axios({ method, url: `${comp.apiPath}/${path}`, data, headers: { Authorization: idToken } }))
      },
      unreadMessages: function (chat) {
        if (!this.userID || !chat) return 0
        const messageCounts = (chat.counts || {}).message || 0
        const userReadCounts = ((chat.readCounts || {})[this.userID] || {}).message || 0
        return Math.max(messageCounts - userReadCounts, 0)
      },
      openEmailEditor: function (project, uuid, template) {
        if (project && this.canUseEmailEditor) {
          this.emailEditorShow = true
          this.emailEditorProject = project
          this.emailEditorUUID = uuid
          this.emailEditorTemplate = JSON.parse(JSON.stringify(template || this.management.emailTemplates[0]))
        } else this.emailEditorShow = false
      },
      editQueuedEmail: function () {
        const comp = this
        this.autoEmailConfirmationDownloading = true
        const uuid = this.autoEmailConfirmation.uuid
        const template = this.autoEmailConfirmation.template

        // Edit queued email before sending
        getDoc(doc(this.db, 'enterprises', this.enterpriseID, 'projects', uuid))
          .then(docSnap => {
            comp.openEmailEditor(docSnap.data(), uuid, template)
            comp.autoEmailConfirmation = null
            comp.autoEmailConfirmationDownloading = false
          })
          .catch(err => {
            comp.autoEmailConfirmationDownloading = false
            console.log('Error triggering automatic email', err)
          })
      },
      shrunkImage: function (src, width, height, format) {
        const fbPrefix = 'https://firebasestorage.googleapis.com'
        let suffix = ''
        const transformSegments = []
        if (width) transformSegments.push('w-' + (width * (window.devicePixelRatio || 1)))
        if (height) transformSegments.push('h-' + (height * (window.devicePixelRatio || 1)))
        if (format) transformSegments.push('f-' + format + ',cm-pad_resize')
        if (transformSegments.length) suffix += 'tr:' + transformSegments.join(',')
        return (src || '').replace(fbPrefix, `https://ik.imagekit.io/incept3d/${suffix}`)
      },
      sendQueuedEmail: function () {
        this.autoEmailConfirmationSending = true

        // Send automatic email
        const comp = this
        this.callAPI('post', `send-automatic-email/${this.$root.enterpriseID}`, {
          project: this.autoEmailConfirmation.uuid,
          trigger: this.autoEmailConfirmation.trigger,
          userID: this.$root.currentUser.uid,
        })
        .then(resp => {
          comp.autoEmailConfirmation = null
          comp.autoEmailConfirmationSending = false
        })
        .catch(err => {
          comp.autoEmailConfirmationSending = false
          console.log('Error triggering automatic email', err)
        })
      },
      triggerEmailPrompt: function (triggerKey, projectLabel, uuid) {
        if (this.management && this.management.emailAutomations && this.management.emailAutomations[triggerKey]) {
          this.management.emailAutomations[triggerKey].forEach(trigger => {
            const template = this.management.emailTemplates.find(x => x.uuid === trigger.template)
            if (template) this.autoEmailConfirmationQueue.push({ template, projectLabel, trigger: triggerKey, uuid })
          })
        }
      },
      openMobileNotification: function (notification) {
        // Switch enterprises if on a different enterprise
        const enterprise = notification.enterprise || this.enterpriseID
        if (enterprise !== this.enterpriseID) {
          localStorage.setItem('notificationToOpen', JSON.stringify(notification))
          this.enterpriseOverride = enterprise
          return
        }

        // Otherwise open notification
        this.notificationClicked(notification)
        if (this.currentUser) {
          updateDoc(
            doc(this.db, 'enterprises', this.enterpriseID, 'users', this.currentUser.uid, 'notifications', notification.uuid),
            {
              read: true,
              readTime: Date.now(),
            },
          ).catch(err => console.log('Error marking notification as read', err))
        }
      },
      notificationClicked: function (n) {
        const app = this
        if (n.task) {
          getDoc(doc(this.db, 'enterprises', this.enterpriseID, 'chats', n.task))
            .then(docSnap => {
              this.navigation.popTask = Object.assign({ uuid: docSnap.id }, docSnap.data())
              if (n.taskElement) this.navigation.popTaskElementHighlight = n.taskElement
              else this.navigation.popTaskShowChatOverride = true
              this.navigation.showPopTask = true
            })

          if (this.subscriptions.popTask) this.subscriptions.popTask()
          this.subscriptions.popTask = onSnapshot(
            doc(this.db, 'enterprises', this.enterpriseID, 'chats', n.task),
            docSnap => {
              this.navigation.popTask = Object.assign({ uuid: docSnap.id }, docSnap.data())
            },
            err => console.log('Error getting pop task', err),
          )
        } else if (n.chat) {
          if (n.threadID) {
            this.$root.messageToScrollToChat = n.chat
            this.$root.messageToScrollTo = n.threadID
          }
          this.openChat(n.chat)
          this.showNotifications = false
        } else if (n.project && this.$router.currentRoute.path !== `/projects/${n.project}`) {
          this.$router.push(`/projects/${n.project}`)
        } else if (n.machine) {
          this.openMachinePanel(n.machine)
        } else if (n.techSupport) {
          // Tech support / ticket notification → open that exact ticket. Devs use the /dev
          // console; everyone else uses their /support page. The destination page consumes
          // pendingSupportTicket on mount (see openPendingTicket there).
          this.pendingSupportTicket = n.ticketId ? { enterpriseID: n.enterprise || n._enterprise, ticketID: n.ticketId } : null
          if (this.isDev) {
            if (this.$router.currentRoute.path !== '/dev') this.$router.push('/dev').catch(() => {})
          } else if (this.$router.currentRoute.path !== '/support') {
            this.$router.push('/support').catch(() => {})
          }
          this.showNotifications = false
        } else if (n.externalLink) {
          window.open(n.externalLink, '_blank')
        }
      },
      mergeTwoSorted: function (arr1, arr2, field) {
        const merged = []
        let index1 = 0
        let index2 = 0
        let current = 0
        while (current < (arr1.length + arr2.length)) {
          const isArr1Depleted = index1 >= arr1.length
          const isArr2Depleted = index2 >= arr2.length
          if (!isArr1Depleted && (isArr2Depleted || ((field ? arr1[index1][field] : arr1[index1]) < (field ? arr2[index2][field] : arr2[index2])))) {
            merged[current] = arr1[index1]
            index1++
          } else {
            merged[current] = arr2[index2]
            index2++
          }
          current++
        }
        return merged
      },
      updateMachines: function () {
        if (this.$root.management && this.$root.management.machines) {
          updateDoc(
            doc(this.db, 'enterprises', this.enterpriseID, 'config', 'management'),
            {
              machines: this.$root.management.machines,
              machinesLastUpdatedBy: this.$root.currentUser.uid,
            },
          )
        }
      },
      getJobSettings: function (job, setting) {
        return Array.from(new Set((job.linked || []).map(m => ((m.model || { requestedSettings: {} }).requestedSettings || {})[setting] || 'Unknown')))
      },
      getJobColorName: function (job, setting) {
        return Array.from(new Set((job.linked || []).map(m => this.modelColorName(m.model, m.modelData))))
      },
      getColor: function (color) {
        if (this.catalog) {
          const colors = this.catalog.find(x => x.name === 'Color')
          if (colors) {
            const thisColor = colors.children.find(x => x.name === color)
            if (thisColor) return thisColor
            else return null
          } else return null
        } else return null
      },
      getQrCode: function (value, returnImage) {
        const qr = new QRious({
          value: value,
          size: returnImage || 500,
        })
        return returnImage ? qr.image : qr.toDataURL()
      },
      openReader: function (uuid, element, popup = true) {
        if (uuid) {
          const materialMatch = this.publicTasks.find(x => x.uuid === uuid)
          if (materialMatch) {
            this.readerTask = materialMatch
            if (popup) this.showTaskReader = true
            if (element) this.readerElement = element
          } else {
            getDoc(doc(this.$root.db, 'enterprises', this.$root.enterpriseID, 'chats', uuid))
            .then(snap => {
              if (snap.data()) {
                this.readerTask = Object.assign({ uuid }, snap.data())
                if (popup) this.showTaskReader = true
                if (element) this.readerElement = element
              }
            })
          }
        }
      },
      openChat: function (uuid) {
        // If we hear that the chat opened or mounted, either finish or respond to open
        function receivedFromChat (info) {
          if (info === 'chatOpened') this.$root.$off(uuid, receivedFromChat)
          else if (info === 'mounted') this.$root.$emit(uuid, 'openChat')
        }

        if (uuid) {
          if (uuid.startsWith('project_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            const projectID = `/projects/${uuid.split('_model_')[0].replace('project_', '')}`
            if (this.$router.currentRoute.path !== projectID) this.$router.push(projectID)
          } else if (uuid.startsWith('publicproject_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            const projectID = `/projects/${uuid.split('_model_')[0].replace('publicproject_', '')}`
            if (this.$router.currentRoute.path !== projectID) this.$router.push(projectID)
          } else if (uuid.startsWith('designcollection_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            this.$root.openDesignCollectionChat = uuid.replace('designcollection_', '')
            if (this.$router.currentRoute.path !== `/collection/${uuid}`) this.$router.push(`/collection/${uuid.replace('designcollection_', '')}`)
          } else if (uuid.startsWith('design_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            if (this.$router.currentRoute.path !== `/design/${uuid}`) this.$router.push(`/design/${uuid.replace('design_', '')}`)
          } else if (uuid.startsWith('printer_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            this.openMachinePanel(uuid.replace('printer_', ''))
          } else if (uuid.startsWith('job_')) {
            this.$root.$emit(uuid, 'openChat')
            this.$root.$on(uuid, receivedFromChat)
            this.previewJob(uuid.replace('job_', ''))
          } else {
            this.$root.$emit('openChat', uuid)
            this.initialChat = uuid
          }
        } else {
          this.$root.$emit('openChat', null)
          this.initialChat = null
        }
      },
      getUserShortName: function (id, fallback) {
        if (!id) return fallback === undefined ? '...' : fallback
        this.$root.loadUserInfo(id)
        if (this.$root.lookup.users[id].displayName) return this.$root.lookup.users[id].displayName
        if (this.$root.lookup.users[id].fullName) return this.$root.lookup.users[id].fullName.split(' ')[0]
        return fallback === undefined ? '...' : fallback
      },
      getUserFullName: function (id, fallback) {
        if (!id) return fallback === undefined ? '...' : fallback
        this.$root.loadUserInfo(id)
        if (this.$root.lookup.users[id].fullName) return this.$root.lookup.users[id].fullName
        return fallback === undefined ? '...' : fallback
      },
      getUserProfilePic: function (id, fallback) {
        if (!id) return fallback === undefined ? '' : fallback
        this.$root.loadUserInfo(id)
        if (this.$root.lookup.users[id].profilePic) return this.$root.lookup.users[id].profilePic
        return fallback === undefined ? '' : fallback
      },
      getAccountReferenceName: function (id, fallback) {
        if (!id) return fallback === undefined ? '...' : fallback
        this.$root.loadAccountInfo(id)
        if (this.$root.lookup.accounts[id].staffProfile && this.$root.lookup.accounts[id].staffProfile.fullName) return this.$root.lookup.accounts[id].staffProfile.fullName
        if (this.$root.lookup.accounts[id].userProfile && this.$root.lookup.accounts[id].userProfile.fullName) return this.$root.lookup.accounts[id].userProfile.fullName
        return fallback === undefined ? '...' : fallback
      },
      getOrganizationName: function (id, fallback) {
        if (!id) return fallback === undefined ? '...' : fallback
        this.$root.loadOrganizationInfo(id)
        if (this.$root.lookup.organizations[id].name) return this.$root.lookup.organizations[id].name
        return fallback === undefined ? '...' : fallback
      },
      addonPrice: function (addon) {
        let price = addon.rate
        if (!price && addon.prices) {
          const availablePrices = addon.prices.filter(x => x.price && x.minQuantity <= +addon.quantity).map(x => x.price)
          price = Math.min(...availablePrices)
        }
        return +price
      },
      roundToCents: function (num) {
        return (+(Math.round(+(+num * 100)) * 0.01)).toFixed(2)
      },
      totals: function (project, numeric, overrides = {}) {
        const result = {
          models: 0,
          addons: 0,
          itemSubtotal: 0,
          taxableSubtotal: 0,
          discounts: 0,
          minimumOrderFee: 0,
          expeditionFee: 0,
          shippingFee: 0,
          subtotal: 0,
          tax: 0,
          total: 0,
          payments: 0,
          balance: 0,
        }

        if (!project) return result

        // Price models from either instant price or manual price
        if (project.models) {
          project.models.filter(m => !m.staffOnly).forEach(m => {
            const finalPrice = +this.roundToCents(this.getModelLineTotal(m, project))
            if (!isNaN(finalPrice)) result.models += finalPrice
          })
        }

        // Add addon prices
        if (project.addons) {
          project.addons.forEach(a => {
            let price = a.rate
            if (!price && a.prices) {
              const availablePrices = a.prices.filter(x => x.price && x.minQuantity <= +a.quantity).map(x => x.price)
              price = Math.min(...availablePrices)
            }

            const finalPrice = +this.roundToCents(+price * +a.quantity)
            if (!isNaN(finalPrice)) result.addons += finalPrice
          })
        }

        // Add expedition fees, and calculate subtotal
        if (this.$root.enableAutomatedPricing && this.estimateLeadTime(project).expeditionEligible && project.requestExpedition) {
          const sales = this.$root.production.sales || {};

          if (sales.enableExpedition) {
            if (sales.expeditionFeeType === 'percentage') {
              // percentage fee of subtotal before expedition fee
              result.expeditionFee = (+Number(sales.expeditionFeePercentage || 0) / 100.0) * (result.models + result.addons);
            } else {
              // flat fee (there are only two expedition fee types as of writing this code)
              result.expeditionFee = +Number(sales.expeditionFeeCost || 0);
            }
          }
        }
        result.itemSubtotal = result.models + result.addons + result.expeditionFee

        // Calculate miscellaneous order fees
        const onlyProducts = (project.addons || []).filter(a => a.sku).length && !(project.addons || []).filter(a => !a.sku).length && !(project.attachments || []).length && !(project.models || []).length
        if (this.$root.enableAutomatedPricing && !onlyProducts && project.minimumOrder && result.itemSubtotal < project.minimumOrder) {
          result.minimumOrderFee = project.minimumOrder - result.itemSubtotal
        }

        // Calculate subtotals
        result.subtotal = result.itemSubtotal + result.minimumOrderFee
        result.discountedSubtotal = result.subtotal

        // Add discounts
        if (project.discounts && project.discounts.length) {
          for (let d = 0; d < project.discounts.length; d++) {
            // Calculate discount
            const dis = project.discounts[d].value
            let discountValue = 0

            // Parse value against subtotal
            if (dis.endsWith('%')) discountValue = +this.roundToCents((parseFloat(dis) / 100) * result.subtotal)
            else discountValue = parseFloat(dis)

            // Subtract for subtotal and prevent negatives
            if (!isNaN(discountValue)) {
              result.discountedSubtotal -= discountValue
              result.discounts += discountValue
            }
            if (result.discountedSubtotal < 0) {
              result.discountedSubtotal = 0
              result.discounts = result.subtotal
            }
          }
        }

        // Calculate shipping
        const shippingMethod = project.shippingMethod || overrides.shippingMethod
        if (shippingMethod?.amount) {
          result.shippingFee = +shippingMethod.amount
        }
        const extraShipments = (project.shipments || []).filter(s => s.addToTotal && s.rate_details && s.rate_details.amount)
        extraShipments.forEach(s => {
          result.shippingFee += +s.rate_details.amount
        })

        // Calculate taxable total
        result.taxableSubtotal = result.discountedSubtotal + result.shippingFee

        // Calculate taxes. projectTax(), not `taxCalculation.tax_amount_exclusive / 100`: a
        // truncated stored calculation has no such field, and `undefined / 100` is NaN — which
        // poisons total AND balance, and ProjectHeaderPane's
        // `balance ? 'No Payment Made' : 'Payment Complete'` reads a falsy NaN as PAID.
        result.tax = projectTax({
          taxCalculation: project.checkout?.stripeTaxCalculation || overrides.stripeTaxCalculation,
          taxRate: project.taxRate,
          taxableSubtotal: result.taxableSubtotal,
          taxReversalAmount: project.taxReversalAmount,
        })

        // Calculate total
        result.total = result.taxableSubtotal + result.tax

        // Calculate balance
        if (project.payments) {
          project.payments.forEach(p => {
            const paymentAmount = +this.roundToCents((p.amount_received + p.amount_capturable) / 100)
            if (!isNaN(paymentAmount)) result.payments += paymentAmount
          })
        }

        // Take into account any Zoho invoice payments
        if (!project.payments && project.invoice && project.invoice.balance < project.invoice.total) {
          result.payments = project.invoice.total - project.invoice.balance
        }

        // Take into account 3D Hubs payments
        if (project.checkout && project.checkout.paymentMethod === '3D Hubs') {
          result.payments = result.total
        }

        // Calculate balance
        result.balance = result.total - result.payments

        // Return in numeric or display format
        Object.keys(result).forEach(k => {
          result[k] = this.roundToCents(result[k])
          if (numeric) result[k] = +result[k]
          else if (result[k] === '-0.00') result[k] = '0.00'
        })
        return result
      },
      projectDescription: function (project) {
        let result = ''
        if (project.models && project.models.length > 0) {
          // Models
          const mLen = project.models.length
          result += mLen + (mLen === 1 ? ' model, ' : ' models, ')

          // Units
          const uLen = project.models.map(mod => +mod.quantity).reduce((a, b) => a + b)
          result += uLen + (uLen === 1 ? ' unit' : ' units')

          // Materials
          const good = project.models.filter(model => model.requestedSettings && model.requestedSettings.Material && model.requestedSettings.Color)
          if (good.length > 0) {
            const mats = [...new Set(good.map(model => this.matchOption('Material', model.requestedSettings.Material, true)?.displayName || model.requestedSettings.Material))]
            const cols = [...new Set(good.map(model => this.matchOption('Color', model.requestedSettings.Color, true)?.displayName || model.requestedSettings.Color))]
            if (mats.length === 1 && cols.length === 1) result += `\n${cols[0]} ${mats[0]}`
            else {
              if (mats.length === 1) result += `\nMixed color ${mats[0]}`
              else if (cols.length === 1) result += `\n${cols[0]} mixed material`
              else result += '\nMixed materials'
            }
          }
        } else if ((project.attachments && project.attachments.length > 0) || (project.addons && project.addons.length)) {
          const lengths = []
          if (project.attachments && project.attachments.length) {
            const attLength = project.attachments.length
            lengths.push(attLength + (attLength === 1 ? ' attachment' : ' attachments'))
          }
          if (project.addons && project.addons.length) {
            const attLength = project.addons.length
            lengths.push(attLength + (attLength === 1 ? ' addon' : ' addons'))
          }
          return lengths.join(', ')
        } else {
          result = 'Empty Project'
        }

        return result
      },
      listenToManagement: function () {
        // Grab current list statuses
        if (this.subscriptions.management) this.subscriptions.management()
        this.subscriptions.management = onSnapshot(
          doc(this.db, 'enterprises', this.enterpriseID, 'config', 'management'),
          docSnap => {
            this.management = docSnap.data()
          },
          err => console.log('Error listening to management snapshot', err),
        )
      },
      listenToWorkflowsConfig: function () {
        if (this.subscriptions.workflowsConfig) this.subscriptions.workflowsConfig()
        this.subscriptions.workflowsConfig = onSnapshot(
          doc(this.db, 'enterprises', this.enterpriseID, 'config', 'workflows'),
          docSnap => {
            this.workflowsConfig = docSnap.data() || null
          },
          err => console.log('Error listening to workflows config snapshot', err),
        )
      },
      listenToMachines: function () {
        // Grab current list statuses
        if (this.subscriptions.machines) this.subscriptions.machines()
        this.subscriptions.machines = onSnapshot(
          query(
            collection(this.db, 'enterprises', this.enterpriseID, 'machines'),
            where('active', '==', true),
          ),
          snap => {
            this.allMachineInstances = snap.docs.map(docSnap => Object.assign(docSnap.data(), { id: docSnap.id }))
          },
          err => console.log('Error listening to machines snapshot', err),
        )
      },
      startStaffSession: function () {
        this.listenToTasks()
        this.listenToStarredNotifications()
        this.listenToStaffList()
        this.listenToManagement()
        this.listenToWorkflowsConfig()
        this.listenToMachines()
        this.listenToUnreadNotifications()
      },
      listenToCurrentEnterpriseStaffAccess: function (userID) {
        if (!userID) return
        const hadStaffAccessSnapshot = this.currentEnterpriseStaffAccessLoaded
        if (this.subscriptions.currentEnterpriseStaffAccess) this.subscriptions.currentEnterpriseStaffAccess()
        if (!hadStaffAccessSnapshot) {
          this.currentEnterpriseStaffAccessLoaded = false
          this.currentEnterpriseStaffAccess = null
        }
        this.subscriptions.currentEnterpriseStaffAccess = onSnapshot(
          doc(this.db, 'enterprises', this.enterpriseID, 'staff', userID),
          snap => {
            this.currentEnterpriseStaffAccessLoaded = true
            this.currentEnterpriseStaffAccess = snap.exists() ? +(snap.data().access || 0) : 0
          },
          err => {
            console.log('Error listening to current staff access snapshot', err)
            this.currentEnterpriseStaffAccessLoaded = true
            this.currentEnterpriseStaffAccess = 0
          },
        )
      },
      /**
       * Swaps email invitations for real chat membership. The rooms an inviter
       * picks are stamped with the invitee's EMAIL (`invitedMembers`), because at
       * invite time the invitee has no uid and the invite document itself is not
       * client-readable. On the member's first schedule-only session this replaces
       * that email with their uid, which is what the room query matches on.
       * Idempotent: once claimed, the query returns nothing.
       */
      claimInvitedChatRooms: async function () {
        const uid = this.currentUser?.uid
        const email = (this.currentUser?.email || '').toLowerCase().replace(/\s/g, '')
        if (!uid || !email) return
        try {
          const snap = await getDocs(query(
            collection(this.db, 'enterprises', this.enterpriseID, 'chats'),
            where('invitedMembers', 'array-contains', email),
          ))
          await Promise.all(snap.docs.map(roomDoc => updateDoc(roomDoc.ref, {
            members: arrayUnion(uid),
            invitedMembers: arrayRemove(email),
            lastModified: Date.now(),
            lastUpdatedBy: uid,
          })))
        } catch (err) {
          console.log('Error claiming invited chat rooms', err)
        }
      },
      /**
       * Mirrors listenToCurrentEnterpriseStaffAccess for the schedule-only role.
       * A denial is the EXPECTED path for everyone who is not a schedule-only
       * member (rules gate the read on the row's own membership fields), so the
       * error handler resolves to "not a member" rather than logging noise.
       */
      listenToCurrentEnterpriseScheduleMember: function (userID) {
        if (!userID) return
        const hadSnapshot = this.currentEnterpriseScheduleMemberLoaded
        if (this.subscriptions.currentEnterpriseScheduleMember) this.subscriptions.currentEnterpriseScheduleMember()
        if (!hadSnapshot) {
          this.currentEnterpriseScheduleMemberLoaded = false
          this.currentEnterpriseScheduleMember = null
        }
        this.subscriptions.currentEnterpriseScheduleMember = onSnapshot(
          doc(this.db, 'enterprises', this.enterpriseID, 'scheduleEmployees', userID),
          snap => {
            this.currentEnterpriseScheduleMemberLoaded = true
            this.currentEnterpriseScheduleMember = snap.exists() ? Object.assign({ uid: userID }, snap.data()) : null
          },
          () => {
            // Denied = not a schedule-only member. Expected for staff and customers alike.
            this.currentEnterpriseScheduleMemberLoaded = true
            this.currentEnterpriseScheduleMember = null
            // A denied Firestore listener is TERMINATED - it never fires again.
            // Clear the subscription slot so the sign-in guard (or the invite
            // acceptance path) can attach a fresh one after the membership row
            // is created; leaving the dead unsubscribe here would block every
            // rebind for the rest of the session.
            this.subscriptions.currentEnterpriseScheduleMember = null
          },
        )
      },
      stopStaffSession: function () {
        if (this.subscriptions.staff) {
          this.subscriptions.staff()
          this.subscriptions.staff = null
        }
        if (this.subscriptions.management) {
          this.subscriptions.management()
          this.subscriptions.management = null
        }
        if (this.subscriptions.workflowsConfig) {
          this.subscriptions.workflowsConfig()
          this.subscriptions.workflowsConfig = null
        }
        if (this.subscriptions.machines) {
          this.subscriptions.machines()
          this.subscriptions.machines = null
        }
        if (this.subscriptions.starredNotifications) {
          this.subscriptions.starredNotifications()
          this.subscriptions.starredNotifications = null
        }
        if (this.subscriptions.unreadNotifications) {
          this.subscriptions.unreadNotifications()
          this.subscriptions.unreadNotifications = null
        }
        if (this.subscriptions.tasks) {
          this.subscriptions.tasks.forEach(x => x && x())
          this.subscriptions.tasks = []
        }
        this.staff = {}
        this.management = null
        this.workflowsConfig = null
        this.allMachineInstances = []
        this.starredUserNotifications = []
        this.unreadUserNotifications = []
        this.userTaskGroups = []
      },
      stopAllUserSubscriptions: function () {
        Object.values(this.subscriptions).forEach(unsub => {
          if (Array.isArray(unsub)) unsub.forEach(u => typeof u === 'function' && u())
          else if (typeof unsub === 'function') unsub()
        })
        this.subscriptions = {}

        Object.keys(this.staffEnterpriseListeners).forEach(k => {
          if (typeof this.staffEnterpriseListeners[k] === 'function') this.staffEnterpriseListeners[k]()
          this.$delete(this.staffEnterpriseListeners, k)
        })
        Object.keys(this.staffEnterpriseNotificationListeners).forEach(k => {
          if (typeof this.staffEnterpriseNotificationListeners[k] === 'function') this.staffEnterpriseNotificationListeners[k]()
          this.$delete(this.staffEnterpriseNotificationListeners, k)
        })
        this.allEnterprises = []
        if (this.jobPreviewListener) {
          this.jobPreviewListener()
          this.jobPreviewListener = null
        }
        if (this.visibleTaskListener) {
          this.visibleTaskListener()
          this.visibleTaskListener = null
        }
        this.staffEnterpriseNames = {}
        this.staffEnterpriseURLs = {}
        this.staffEnterpriseUnreadNotificationCount = {}
      },
      listenToStaffList: function () {
        // Grab current staff list
        if (this.subscriptions.staff) this.subscriptions.staff()
        this.subscriptions.staff = onSnapshot(
          collection(this.db, 'enterprises', this.enterpriseID, 'staff'),
          docs => {
            const staff = {}
            docs.forEach(docSnap => {
              staff[docSnap.id] = docSnap.data()
              this.loadUserInfo(docSnap.id)
            })
            this.staff = staff
          },
          err => console.log('Error listening to staff snapshot', err),
        )
      },
      listenToStarredNotifications: function () {
        const app = this
        if (this.subscriptions.starredNotifications) this.subscriptions.starredNotifications()
        this.subscriptions.starredNotifications = onSnapshot(
          query(collection(db, 'enterprises', app.enterpriseID, 'users', this.currentUser.uid, 'notifications'), orderBy('created', 'desc'), where('flag', '==', true)),
          docs => {
            const notifications = []
            docs.forEach(docSnap => {
              const notif = docSnap.data()
              notif.uuid = docSnap.id
              notifications.push(notif)
            })
            app.starredUserNotifications = notifications
            app.updateNotifications()
          },
          err => console.log('Error listening to starred notifications snapshot', err),
        )
      },
      listenToUnreadNotifications: function () {
        if (this.subscriptions.unreadNotifications) this.subscriptions.unreadNotifications()
        this.subscriptions.unreadNotifications = onSnapshot(
          query(collection(db, 'enterprises', app.enterpriseID, 'users', this.currentUser.uid, 'notifications'), where('read', '==', false), orderBy('created', 'desc')),
          snap => {
            const notifications = []
            snap.docs.forEach(docSnap => {
              const notif = docSnap.data()
              notif.uuid = docSnap.id
              notifications.push(notif)
            })
            this.unreadUserNotifications = notifications
            this.updateNotifications()
          },
          err => console.log('Error listening to starred notifications snapshot', err),
        )
      },
      listenToNotifications: function () {
        const app = this
        if (this.subscriptions.notifications) this.subscriptions.notifications()
        app._notificationsHydrated = false
        this.subscriptions.notifications = onSnapshot(
          query(collection(this.db, 'enterprises', this.enterpriseID, 'users', this.currentUser.uid, 'notifications'), orderBy('created', 'desc'), limit(app.notificationAmount * app.notificationPage)),
          snap => {
            // Ding once for genuinely-new notifications. Skip the first (hydration)
            // snapshot, our own pending writes, and read-state changes (those arrive
            // as 'modified', not 'added'); the service throttles bursts to one sound.
            if (app._notificationsHydrated) {
              const fresh = snap.docChanges().filter(c =>
                c.type === 'added' && !c.doc.metadata.hasPendingWrites && !(c.doc.data() && c.doc.data().read)
              )
              if (fresh.length > 0) notificationSound.notifyArrival(app)
            }
            app._notificationsHydrated = true
            if (snap.docs.length >= app.userNotifications.length) {
              const notifications = []
              snap.forEach(docSnap => {
                const notif = docSnap.data()
                notif.uuid = docSnap.id
                notifications.push(notif)
              })
              app.userNotifications = notifications
              app.updateNotifications()
            }
          },
          err => console.log('Error listening to notifications snapshot', err),
        )
      },
      listenToGlobalNotifications: function () {
        if (this.subscriptions.globalNotifications) this.subscriptions.globalNotifications()
        this.subscriptions.globalNotifications = onSnapshot(
          query(collectionGroup(this.db, 'enterprises', this.enterpriseID, 'users', this.currentUser.uid, 'notifications'), orderBy('created', 'desc'), limit(app.notificationAmount * app.notificationPage)),
          snap => {
            if (snap.docs.length >= app.userNotifications.length) {
              const notifications = []
              snap.forEach(docSnap => {
                const notif = docSnap.data()
                notif.uuid = docSnap.id
                notifications.push(notif)
              })
              app.userNotifications = notifications
              app.updateNotifications()
            }
          },
          err => console.log('Error listening to notifications snapshot', err),
        )
      },
      listenToTeams: function () {
        const app = this
        if (this.subscriptions.teams) this.subscriptions.teams()
        this.subscriptions.teams = onSnapshot(
          query(
            collection(this.db, 'enterprises', this.enterpriseID, 'teams'),
            where('active', '==', true),
            orderBy('created'),
          ),
          snap => {
            app.allTeams = snap.docs.map(x => Object.assign({ uuid: x.id }, x.data())).concat([{ uuid: 'public', name: 'Public' }])
            app.listenToTasks()
          },
          err => console.log('Error listening to teams snapshot', err),
        )
      },
      listenToTasks: function () {
        // Listen to tasks that are available to the user/user's teams
        // Because the list of teams might be more than 10, we'll need to split the query into multiple queries

        if (this.subscriptions.tasks) this.subscriptions.tasks.forEach(x => x())
        this.subscriptions.tasks = []
        const memberIDs = (this.currentUser ? [this.currentUser.uid] : ['public']).concat(this.myTeams.map(x => x.uuid))

        // Split memberIDs into groups of 10
        const memberIDGroups = []
        while (memberIDs.length > 0) memberIDGroups.push(memberIDs.splice(0, 10))

        this.userTaskGroups = []
        memberIDGroups.forEach((memberIDGroup, i) => {
          this.subscriptions.tasks[i] = onSnapshot(
            query(
              collection(this.db, 'enterprises', this.enterpriseID, 'chatsMinified'),
              or(
                and(
                  where('type', '==', 'task'),
                  where('taskUsers', 'array-contains-any', memberIDGroup),
                  where('active', '==', true),
                ),
                and(
                  where('active', '==', true),
                  where('taggedUsers', 'array-contains-any', memberIDGroup),
                )
              )
            ),
            snapshot => {
              this.userTaskGroups.splice(i, 1, snapshot.docs.map(d => Object.assign(d.data(), { uuid: d.id })))
            },
            err => console.log('Error listening to user tasks snapshot of member group', memberIDGroup, err),
          )
        })
      },
      startStaffIdleTimeout: function () {
        this.stopStaffIdleTimeout()
        if (!this.staffIdleTimeoutEnabled || !this.staffIdleTimeoutStorageKey) return

        const timeoutMs = this.staffIdleTimeoutMinutes * 60 * 1000
        const storedLastActivity = Number(window.localStorage.getItem(this.staffIdleTimeoutStorageKey) || 0)
        const skipStoredSessionCheck = this.staffIdleTimeoutFreshLogin
        this.staffIdleTimeoutFreshLogin = false
        if (!skipStoredSessionCheck && !this.staffIdleTimeoutEvaluatedStoredSession && storedLastActivity && Date.now() - storedLastActivity >= timeoutMs) {
          this.staffIdleTimeoutEvaluatedStoredSession = true
          this.logoutForStaffIdleTimeout()
          return
        }
        this.staffIdleTimeoutEvaluatedStoredSession = true

        this.recordStaffIdleActivity(true)
        this.staffIdleTimeoutActivityHandler = () => this.recordStaffIdleActivity()
        this.staffIdleTimeoutVisibilityHandler = () => {
          if (!document.hidden) this.recordStaffIdleActivity()
        }

        const activityEvents = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart', 'click', 'input', 'change']
        activityEvents.forEach(eventName => window.addEventListener(eventName, this.staffIdleTimeoutActivityHandler, { passive: true, capture: true }))
        document.addEventListener('visibilitychange', this.staffIdleTimeoutVisibilityHandler)

        this.staffIdleTimeoutListenerActive = true
        this.staffIdleTimeoutTimer = setInterval(() => this.checkStaffIdleTimeout(), 1000)
      },
      stopStaffIdleTimeout: function () {
        if (this.staffIdleTimeoutActivityHandler) {
          const activityEvents = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart', 'click', 'input', 'change']
          activityEvents.forEach(eventName => window.removeEventListener(eventName, this.staffIdleTimeoutActivityHandler, { capture: true }))
        }
        if (this.staffIdleTimeoutVisibilityHandler) document.removeEventListener('visibilitychange', this.staffIdleTimeoutVisibilityHandler)
        if (this.staffIdleTimeoutTimer) clearInterval(this.staffIdleTimeoutTimer)
        if (this.staffIdleTimeoutCountdownTimer) clearInterval(this.staffIdleTimeoutCountdownTimer)

        this.staffIdleTimeoutListenerActive = false
        this.staffIdleTimeoutTimer = null
        this.staffIdleTimeoutCountdownTimer = null
        this.staffIdleTimeoutActivityHandler = null
        this.staffIdleTimeoutVisibilityHandler = null
        this.staffIdleTimeoutWarningOpen = false
        this.staffIdleTimeoutWarningSecondsLeft = 60
      },
      recordStaffIdleActivity: function (force) {
        if (!this.staffIdleTimeoutEnabled || !this.staffIdleTimeoutStorageKey) return
        const now = Date.now()
        if (!force && now - this.staffIdleTimeoutLastActivityWrite < 5000) return
        this.staffIdleTimeoutLastActivityWrite = now
        window.localStorage.setItem(this.staffIdleTimeoutStorageKey, String(now))
        if (this.staffIdleTimeoutWarningOpen) this.cancelStaffIdleTimeoutWarning()
      },
      checkStaffIdleTimeout: function () {
        if (!this.staffIdleTimeoutEnabled || !this.staffIdleTimeoutStorageKey || this.staffIdleTimeoutLogoutInProgress) return
        if (this.staffIdleTimeoutUploadsActive) {
          this.recordStaffIdleActivity(true)
          return
        }

        const timeoutMs = this.staffIdleTimeoutMinutes * 60 * 1000
        const lastActivity = Number(window.localStorage.getItem(this.staffIdleTimeoutStorageKey) || Date.now())
        if (Date.now() - lastActivity >= timeoutMs && !this.staffIdleTimeoutWarningOpen) this.openStaffIdleTimeoutWarning()
      },
      openStaffIdleTimeoutWarning: function () {
        if (!this.staffIdleTimeoutEnabled || this.staffIdleTimeoutLogoutInProgress) return
        if (this.staffIdleTimeoutCountdownTimer) clearInterval(this.staffIdleTimeoutCountdownTimer)

        this.staffIdleTimeoutWarningSecondsLeft = 60
        this.staffIdleTimeoutWarningOpen = true
        this.staffIdleTimeoutCountdownTimer = setInterval(() => {
          this.staffIdleTimeoutWarningSecondsLeft--
          if (this.staffIdleTimeoutWarningSecondsLeft <= 0) {
            if (this.staffIdleTimeoutCountdownTimer) clearInterval(this.staffIdleTimeoutCountdownTimer)
            this.staffIdleTimeoutCountdownTimer = null
            this.logoutForStaffIdleTimeout()
          }
        }, 1000)
      },
      cancelStaffIdleTimeoutWarning: function () {
        if (this.staffIdleTimeoutCountdownTimer) clearInterval(this.staffIdleTimeoutCountdownTimer)
        this.staffIdleTimeoutCountdownTimer = null
        this.staffIdleTimeoutWarningOpen = false
        this.staffIdleTimeoutWarningSecondsLeft = 60
      },
      staySignedInAfterStaffIdleWarning: function () {
        this.recordStaffIdleActivity(true)
        this.cancelStaffIdleTimeoutWarning()
      },
      logoutForStaffIdleTimeout: function () {
        if (this.staffIdleTimeoutLogoutInProgress) return
        this.staffIdleTimeoutLogoutInProgress = true
        this.cancelStaffIdleTimeoutWarning()
        this.logout()
          .catch(err => console.log('Error logging out from staff idle timeout', err))
          .finally(() => {
            this.staffIdleTimeoutLogoutInProgress = false
          })
      },
      /**
       * @param {object} [loginQuery] Optional query for the Login redirect, so a
       * caller can explain WHY the session ended (e.g. the schedule-only
       * containment's `noScheduleAccess`). Logout owns that navigation - a caller
       * racing its own push against the replace below loses the query.
       */
      logout: async function (loginQuery) {
        this.stopStaffIdleTimeout()
        if (this.currentUser?.uid && this.notificationToken) {
          setDoc(
            doc(this.db, 'users', this.currentUser.uid, 'devices', this.notificationToken),
            { active: false, dateModified: Date.now() },
            { merge: true },
          ).catch(err => console.log('Error logging out of device', err))
        }
        this.loggingOut = true
        // Signing out drops the ff_ui choice, except when the staff idle timeout is what signed
        // them out. The choice belongs to the person, not the machine: after a deliberate
        // sign-out the next staff member on a shared browser gets their enterprise's default
        // rather than inheriting this session's opt-out (Vue 3 does the same in
        // signOutEverywhere). An idle timeout is the same person stepping away and signing
        // straight back in, so their opt-out has to survive it. Every idle-driven logout comes
        // through logoutForStaffIdleTimeout, which raises this flag before calling us.
        if (!this.staffIdleTimeoutLogoutInProgress) clearVue3Cookie()
        this.currentEnterpriseStaffAccessLoaded = true
        this.currentEnterpriseStaffAccess = 0
        this.state.settingsOpen = false
        this.showProjectPreview = false
        if (this.navigation) {
          this.navigation.showPopTask = false
          this.navigation.popTask = null
          this.navigation.popTaskShowChatOverride = null
          this.navigation.popTaskElementHighlight = null
        }
        try {
          if (this.$route.name !== 'Login' || loginQuery) await this.$router.replace({ name: 'Login', query: loginQuery }).catch(() => {})
          await this.$nextTick()
          this.stopStaffSession()
          this.stopAllUserSubscriptions()
          await signOut(this.auth)
          this.enterpriseOverride = null
        } catch (err) {
          console.log(err)
          this.loggingOut = false
        }
      },
      updateNotifications: function () {
        if (app.showNotifications) {
          app.userNotifications.forEach(n => {
            if (!n.read) {
              updateDoc(doc(app.db, 'enterprises', app.enterpriseID, 'users', app.currentUser.uid, 'notifications', n.uuid), {
                read: true,
                readTime: Date.now(),
              }).catch(err => console.log('Error marking notification as read', err))
            }
          })
        }
      },
      readUnreadNotifications: function () {
        this.unreadUserNotifications.forEach(n => {
          if (!n.read) {
            updateDoc(doc(this.db, 'enterprises', this.enterpriseID, 'users', this.currentUser.uid, 'notifications', n.uuid), {
              read: true,
              readTime: Date.now(),
            }).catch(err => console.log('Error marking notification as read', err))
          }
        })
      },
      // Mark this user's unread notifications for a ticket as read, so opening the ticket clears it
      // from the bell without opening the popup. Mirrors the single-notification markRead at
      // src/main.js:3235. Notifications live under the TICKET's enterprise (userTools.js:17), so we
      // query that known subcollection path directly. Single equality filter on ticketId → automatic
      // index, no composite index needed; read-state is filtered in memory.
      markTicketNotificationsRead: async function (ticketEnterpriseId, ticketUuid) {
        if (!this.currentUser || !ticketEnterpriseId || !ticketUuid) return
        try {
          const snap = await getDocs(query(
            collection(this.db, 'enterprises', ticketEnterpriseId, 'users', this.currentUser.uid, 'notifications'),
            where('ticketId', '==', ticketUuid),
          ))
          snap.forEach(d => {
            if (d.data().read === false) {
              updateDoc(d.ref, { read: true, readTime: Date.now() })
                .catch(err => console.log('Error clearing ticket notification', err))
            }
          })
        } catch (err) {
          console.log('Error querying ticket notifications', err)
        }
      },
      // --- All-enterprises list (developers only) -------------------------------------------
      // Devs handle support tickets from every enterprise. This lean listener just tracks the
      // active enterprise list so the Notification Center can let a dev scope to any of them; the
      // per-enterprise notification reads happen there, on demand — not always-on.
      startDevTicketFeed: function () {
        if (this.subscriptions.allEnterprises) return
        // No orderBy/where so we don't depend on a composite index (mirrors DevDashboard.loadEnterprises);
        // active-filtering happens in memory.
        this.subscriptions.allEnterprises = onSnapshot(
          collection(this.db, 'enterprises'),
          snap => {
            this.allEnterprises = snap.docs
              .map(d => {
                const data = d.data() || {}
                const primaryDomain = ((data.domains || []).find(x => x && x.domain) || {}).domain || ''
                const name = data.name && data.name !== 'formfactories' ? data.name : data.description
                return { id: d.id, name: name || d.id, url: primaryDomain, active: data.active }
              })
              .filter(e => e.active !== false)
          },
          err => console.log('Error listening to enterprises for dev ticket feed', err),
        )
      },
      stopDevTicketFeed: function () {
        if (this.subscriptions.allEnterprises) {
          this.subscriptions.allEnterprises()
          this.subscriptions.allEnterprises = null
        }
        this.allEnterprises = []
      },
      loadAccountInfo: function (id) {
        if (id && !this.$root.lookup.accounts[id]) {
          app.$set(this.$root.lookup.accounts, id, {})
          const data = this
          if (!this.$root.subscriptions['account' + id]) {
            this.$root.subscriptions['account' + id] = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'accounts', id),
              docSnap => {
                this.$root.lookup.accounts[id] = docSnap.data()
              },
              err => console.log('Error in downloading account data:', err))
          }
        }
      },
      // enterpriseId is OPTIONAL. When omitted, user info is read from the current
      // enterprise (regular behavior). The dev dashboard passes a foreign enterprise so
      // cross-enterprise senders resolve to a real name instead of '...'. The first caller
      // for a given uid wins the lookup slot, so ChatMessage preloads the sender from the
      // correct enterprise (in its created hook) before any name helper runs.
      loadUserInfo: function (uuid, enterpriseId) {
        if (uuid && !this.$root.lookup.users[uuid]) {
          app.$set(this.$root.lookup.users, uuid, {})
          if (!this.$root.subscriptions['user' + uuid]) {
            this.$root.subscriptions['user' + uuid] = onSnapshot(
              doc(this.db, 'enterprises', enterpriseId || this.enterpriseID, 'users', uuid),
              docSnap => {
                this.$root.lookup.users[uuid] = docSnap.data() || {}
              },
              err => console.log('Error in downloading user data:', err))
          }
        }
      },
      loadOrganizationInfo: function (id) {
        if (id && !this.$root.lookup.organizations[id]) {
          this.$set(this.$root.lookup.organizations, id, {})
          if (!this.$root.subscriptions['organization' + id]) {
            this.$root.subscriptions['organization' + id] = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'organizations', id),
              docSnap => {
                this.$root.lookup.organizations[id] = docSnap.data()
              },
              err => console.log('Error in downloading organization data:', err))
          }
        }
      },
      markSupportRead: function (key) {
        if (!key || !this.currentUser) return
        // Optimistic local update so badges clear immediately.
        this.$set(this.supportReads, key, Date.now())
        setDoc(doc(db, 'supportReads', this.currentUser.uid), { [key]: Date.now() }, { merge: true })
          .catch(err => console.log('Error marking support read', err))
      },
      loadRoomInfo: function (uuid) {
        if (uuid && !this.$root.lookup.chatRooms[uuid]) {
          this.$set(this.$root.lookup.chatRooms, uuid, { uuid: uuid })
          if (!this.$root.subscriptions['chat' + uuid]) {
            this.$root.subscriptions['chat' + uuid] = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'chats', uuid),
              docSnap => {
                this.$root.lookup.chatRooms[uuid] = Object.assign(docSnap.data() || {}, { uuid: uuid })
              },
              err => console.log('Error in downloading chat room data:', err),
            )
          }
        }
      },
      getJob: function (id) {
        const emptyReturn = {}
        if (id) {
          if (this.$root.lookup.jobs[id]) return this.$root.lookup.jobs[id]
          else if (this.$root.subscriptions['job' + id]) return emptyReturn
          else {
            const app = this
            this.$root.subscriptions['job' + id] = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'jobs', id),
              docSnap => {
                this.$set(this.$root.lookup.jobs, id, Object.assign({ uuid: docSnap.id }, docSnap.data()))
              },
              err => console.log('Error getting job', err),
            )
            return emptyReturn
          }
        } else return emptyReturn
      },
      getUser: function (id) {
        const emptyReturn = {}
        if (id) {
          if (this.$root.lookup.users[id]) return this.$root.lookup.users[id]
          else if (this.$root.subscriptions['user' + id]) return emptyReturn
          else {
            const app = this
            this.$root.subscriptions['user' + id] = onSnapshot(
              doc(this.db, 'enterprises', this.enterpriseID, 'users', id),
              docSnap => {
                this.$set(this.$root.lookup.users, id, Object.assign({ uuid: docSnap.id }, docSnap.data()))
              },
              err => console.log('Error getting user', err),
            )
            return emptyReturn
          }
        } else return emptyReturn
      },
    },
    render: h => h(App),
  }).$mount('#app')
}

export {
  app,
}
